/**
 * @file GenerateStatements.js
 * @version 3.21.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-12-05
 * @modified 2026-10-20
 *
 * 3.15.0 (2026-10-12): "Hand over…" — a run, at whatever stage, can be handed
 * to colleagues or shared. Its progress (step, contacts, invoices left out,
 * delivery choices, extra PDFs) is kept on the run (useStatementRunWork), not
 * only in this tab; approvals and notes are written with the value shown, so
 * a stale screen is refused rather than obeyed.
 * 3.16.0 (2026-10-12): review fixes — Take it back / Take over this run,
 * choices made before the run loads are not overwritten by it, a reset draft
 * is closed, extra PDFs always reach the send (run's + this tab's, once each).
 * 3.17.0 (2026-10-19): the statement Xero-check (hooks/useStatementXeroCheck):
 * "Sync with Xero" on steps 1 and 2, a "Xero check" column with statements
 * that don't match pinned to the top in red, "approve anyway?" for one of
 * those, bulk approve leaving them out (and saying which), deliveries and
 * bulk approve waiting for a running check unless told to go ahead.
 * 3.17.1 (2026-10-19): the review flags (incl. Accounts' "paid just after
 * the period end" and "credit not allocated while owing") show on step 2 too.
 * 3.18.0 (2026-10-19): the Accounts step — "Hand over to Accounts" on step 2
 * (the hand-over window in Accounts mode; the run is then "With Accounts"), a
 * banner on steps 2 and 3 while it is, and the run's Accounts confirmation
 * ("Worldpay reconciled in Xero up to <period end> and the Accounts flags
 * reviewed", StatementAccountsPanel) on both steps. Emailing customers and
 * printing are greyed with the reason until it is ticked; the server refuses
 * them (ACCOUNTS_NOT_CONFIRMED) whatever the screen says.
 * 3.19.0 (2026-10-20, Cam — Statement Runs Redesign): four steps (Set up,
 * Contents, Review, Send) in StatementRunFrame — one header, the checks as
 * chips with their detail in a window, a body that fills the screen, Back and
 * Next fixed in the footer. Month-end or interim (utils/statementRunKind).
 * Contents before Review so every preview is what goes; fully-used credit
 * notes listed / attached by choice. Review: table beside the PDF, A / U / N
 * and arrows. Send by contact group (emails, then the print). Also fixes
 * generation stopping at "follow is not a function" (xeroCheck.check.follow).
 * 3.19.1 (2026-10-20, Cam): Review leaves the Xero check out on an interim run
 * (no column, filter or chip); the table's actions sit in an inner div.
 * 3.20.0 (2026-10-20, Cam — the statements look): the approved redesign. The
 * header's tools are slide-outs (Hand over and To Accounts labelled), the
 * people on the run are initials in the header, the footer carries an icon
 * and (on Review) a progress bar. Review's selection ticks feed a bulk bar
 * (onBulk { ids, action, scope: 'selected' }, ticking every statement uses
 * approve-all / unapprove-all) and bundling invoices for ticked rows
 * (table.onSetInclude). Send's checks are keyed so the Accounts confirmation
 * sits inside its own item.
 * 3.21.0 (2026-10-20, Cam — statement notes): two notes per statement in one
 * window (StatementNoteDialog, from the row, N and the preview): the office
 * note (only seen here — the approval table shows it) and the note for the
 * customer (printed on the statement and in the email). Each saved with its own
 * stale-screen guard. The preview is drawn again when the customer note
 * changes.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { API_URL } from '../config';
import Input from './Input';
import ReviewFlagsPanel from './ReviewFlagsPanel';
import './GenerateStatements.css';
import LoadingBar from './LoadingBar';
import LoadingSpinner from './LoadingSpinner';
import Modal from './Modal';
import ContactModal from './ContactModal';
// dateFormatters import removed — formatPeriod no longer needed
import AlertDialog from './AlertDialog';
import ConfirmDialog from './ConfirmDialog';
import { useSlideOutButton } from './SlideOutButton';
import usePersistedState from '../hooks/usePersistedState';
import { useNavStore } from '../store/navStore';
import StatementPrintSummary, { printHeadline } from './StatementPrintSummary';
import { compareStatements, accountLabel } from '../utils/statementOrder';
import StatementSyncPanel from './StatementSyncPanel';
import useStatementXeroCheck, { bulkApprovalText } from '../hooks/useStatementXeroCheck';
import { integrityRank, summariseIntegrity } from '../utils/statementIntegrity';
import useStatementRunWork from '../hooks/useStatementRunWork';
import StatementHandOverDialog from './StatementHandOverDialog';
import StatementNoteDialog from './StatementNoteDialog';
import StatementHandOverNotices from './StatementHandOverNotices';
import StatementRunPeople from './StatementRunPeople';
import StatementAccountsPanel from './StatementAccountsPanel';
import StatementRunFrame from './StatementRunFrame';
import StatementStepSetup from './StatementStepSetup';
import StatementStepContents from './StatementStepContents';
import StatementStepReview from './StatementStepReview';
import StatementStepSend from './StatementStepSend';
import StatementChecksDrawer from './StatementChecksDrawer';
import {
  normaliseRunKind, isInterim, RUN_KIND_LABEL, runKindBlock, routingPlan, CONTENT_DEFAULTS
} from '../utils/statementRunKind';
import {
  includedStatementIds, datesComplete, officeEmailToStore, handOverBlock, handOverResultText, stageText,
  periodLabel, handOverNoticeText, ACCOUNTS_HANDOVER_MESSAGE, accountsConfirmationText, accountsToggleBlock,
  accountsSendBlock, accountsHandOverBlock, STEP_NAMES, accountsNotRequiredText
} from '../utils/statementRunWork';

// One-time migration: the workflow used to live under a single localStorage
// key (generateStatements_workflowState). New code splits it across nav-store
// slots in sessionStorage. If a user has an in-progress workflow under the
// old key, copy it across so they don't lose their place on first load.
// Runs at module-load time so the values are in the store before the
// component's usePersistedState calls read it.
(() => {
  try {
    const LEGACY_KEY = 'generateStatements_workflowState';
    const raw = localStorage.getItem(LEGACY_KEY);
    if (!raw) return;
    const legacy = JSON.parse(raw);
    const store = useNavStore.getState();
    const haveAny = ['step', 'dates', 'runId', 'selectedContacts']
      .some(k => store.pages[`generateStatements.${k}`] !== undefined);
    if (!haveAny) {
      if (legacy.step !== undefined)  store.setPage('generateStatements.step', legacy.step);
      if (legacy.dates !== undefined) store.setPage('generateStatements.dates', legacy.dates);
      if (legacy.runId !== undefined) store.setPage('generateStatements.runId', legacy.runId);
      if (legacy.selectedContacts !== undefined) store.setPage('generateStatements.selectedContacts', legacy.selectedContacts);
    }
    localStorage.removeItem(LEGACY_KEY);
  } catch (e) {
    // Best-effort migration; ignore failures.
  }
})();

// Helper component for rendering email results with copy-to-clipboard
const EmailResultSummary = ({ results, onCopy }) => {
  const successful = results.filter(r => r.success);
  const failed = results.filter(r => !r.success);

  const formatResultsForClipboard = useCallback(() => {
    const lines = [];
    lines.push(`Email Send Results - ${new Date().toLocaleString('en-GB')}`);
    lines.push('='.repeat(50));
    lines.push(`Total: ${results.length} | Sent: ${successful.length} | Failed: ${failed.length}`);
    lines.push('');

    if (failed.length > 0) {
      lines.push('FAILED:');
      failed.forEach(r => {
        lines.push(`  ${accountLabel(r.account, r.customer)}: ${r.error}`);
      });
      lines.push('');
    }

    if (successful.length > 0) {
      lines.push('SENT:');
      successful.forEach(r => {
        lines.push(`  ${accountLabel(r.account, r.customer)}`);
      });
    }

    return lines.join('\n');
  }, [results, successful, failed]);

  const handleCopy = async () => {
    const text = formatResultsForClipboard();
    try {
      await navigator.clipboard.writeText(text);
      if (onCopy) onCopy();
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div style={{ textAlign: 'left', maxHeight: '300px', overflowY: 'auto' }}>
      <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: failed.length > 0 ? '#fff3cd' : '#d4edda', borderRadius: '6px' }}>
        <strong>Summary:</strong> {successful.length} sent, {failed.length} failed
      </div>

      {failed.length > 0 && (
        <div style={{ marginBottom: '15px' }}>
          <div style={{ fontWeight: '600', color: '#dc3545', marginBottom: '8px' }}>Failed ({failed.length}):</div>
          {failed.map((r, idx) => (
            <div key={idx} style={{ padding: '6px 10px', backgroundColor: '#f8d7da', borderRadius: '4px', marginBottom: '4px', fontSize: '13px' }}>
              {r.account ? <><strong>{r.account}</strong> - </> : null}{r.customer}
              <div style={{ color: '#721c24', fontSize: '12px', marginTop: '2px' }}>{r.error}</div>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={handleCopy}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
          width: '100%',
          padding: '10px',
          backgroundColor: '#1E3A5F',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: '500'
        }}
      >
        <span className="material-symbols-rounded" style={{ fontSize: '18px' }}>content_copy</span>
        Copy Results to Clipboard
      </button>
    </div>
  );
};

/** Today's London day, 'YYYY-MM-DD'. */
const londonToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());

// "Sat 3 Oct, 14:05" in London time.
const asAtText = (iso) => {
  if (!iso) return 'unknown';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'unknown';
  return d.toLocaleString('en-GB', {
    timeZone: 'Europe/London', weekday: 'short', day: 'numeric', month: 'short',
    hour: '2-digit', minute: '2-digit'
  });
};

/*
 * How fresh the run's figures were when it was generated: the server brings
 * payments and invoices in from Xero first (services/arFreshnessService.js)
 * and stamps the run. When Xero could not be reached the statements were
 * still made, from the figures held — this says how old those were, so nobody
 * sends them believing they are up to the minute.
 */
const FiguresAsAtNote = ({ stamp }) => {
  if (!stamp) return null;
  const old = !!stamp.stale;
  const missed = !stamp.refreshedFromXero;
  const tone = old ? 'stale' : (missed ? 'missed' : 'fresh');
  const icon = old ? 'warning' : (missed ? 'cloud_off' : 'cloud_done');
  return (
    <div className={`figures-as-at ${tone}`} role={old ? 'alert' : 'status'}>
      <span className="material-symbols-rounded">{icon}</span>
      <span>
        Payments as at <strong>{asAtText(stamp.paymentsAsAt)}</strong>
        {missed
          ? <> — Xero could not be reached when these were generated{stamp.xeroError ? ` (${stamp.xeroError})` : ''}, so these are the figures last brought in.</>
          : <> — brought in from Xero when these were generated.</>}
        {old && <> They were over two hours old: if Xero still cannot be reached, printing or emailing will ask you to confirm.</>}
      </span>
    </div>
  );
};

function GenerateStatements({ user }) {
  // Resume-prompt logic — the workflow's load/save mechanism used to be a
  // hand-rolled localStorage round-trip under WORKFLOW_STATE_KEY. It now
  // rides the central nav store via usePersistedState (one slot per field,
  // sessionStorage-backed). We still surface the "Start fresh or continue?"
  // prompt on mount so users get the same decision they did before — see
  // showResumePrompt below.
  const clearWorkflowState = () => {
    const store = useNavStore.getState();
    store.clearPage('generateStatements.step');
    store.clearPage('generateStatements.dates');
    store.clearPage('generateStatements.runId');
    store.clearPage('generateStatements.selectedContacts');
    store.clearPage('generateStatements.runKind');
  };

  // syncStatus removed — sync step eliminated

  // Get first and last day of previous month
  const getPreviousMonthRange = () => {
    const now = new Date();
    const year = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
    const month = now.getMonth() === 0 ? 12 : now.getMonth();
    const firstDay = `${year}-${String(month).padStart(2, '0')}-01`;
    const daysInMonth = new Date(year, month, 0).getDate();
    const lastDay = `${year}-${String(month).padStart(2, '0')}-${daysInMonth}`;
    return { start: firstDay, end: lastDay };
  };

  // Get month range for a specific offset (0 = current month, -1 = last month, etc.)
  const getMonthRange = (monthOffset) => {
    const now = new Date();
    const targetDate = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
    const year = targetDate.getFullYear();
    const month = targetDate.getMonth() + 1;
    const firstDay = `${year}-${String(month).padStart(2, '0')}-01`;
    const daysInMonth = new Date(year, month, 0).getDate();
    const lastDay = `${year}-${String(month).padStart(2, '0')}-${daysInMonth}`;
    return { start: firstDay, end: lastDay };
  };

  // Get abbreviated month name (3 letters)
  const getMonthNameShort = (monthOffset) => {
    const targetDate = new Date();
    targetDate.setMonth(targetDate.getMonth() + monthOffset);
    return targetDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  // Workflow state — persisted via the central nav store, so navigating away
  // and back returns the user to the same step (filters, run, selected
  // contacts). The resume prompt below decides whether to offer them a
  // "continue" path or wipe and start fresh.
  const [dates, setDates] = usePersistedState('generateStatements.dates', getPreviousMonthRange());
  const [step, setStepState] = usePersistedState('generateStatements.step', 1);
  const [runId, setRunIdState] = usePersistedState('generateStatements.runId', null);
  const [selectedContacts, setSelectedContacts] = usePersistedState('generateStatements.selectedContacts', []);
  // Month-end or interim (utils/statementRunKind), chosen on step 1.
  const [runKind, setRunKindState] = usePersistedState('generateStatements.runKind', 'month_end');
  // The statement print this screen is following — { id, runId } — kept in the
  // nav store so that leaving the screen (or reloading) while the printer
  // works does not lose it: on the way back the screen picks the print up
  // again, or, if it has gone, shows what the delivery log says did not print.
  // Cleared when the print ends.
  const [printJob, setPrintJob] = usePersistedState('generateStatements.printJob', null);
  const printJobRef = useRef(printJob);
  printJobRef.current = printJob;
  // The run this screen was on before its dates changed (the run was dropped
  // to be generated again): the next Generate continues it, so the people on
  // it and its progress move to the new run.
  const [continuesRunId, setContinuesRunId] = usePersistedState('generateStatements.continuesRunId', null);
  // The run's progress as the server holds it — shared by everyone on the run.
  const runWork = useStatementRunWork(runId);
  const runIdRef = useRef(runId);
  runIdRef.current = runId;
  const work = runWork.work;
  const workReady = runWork.ready;
  const meId = user?.id ?? null;
  // Compute "has saved state" exactly once on first render — we don't want
  // the prompt to re-open every time `step` changes during this session.
  const initialStepRef = useRef(step);
  const [showResumePrompt, setShowResumePrompt] = useState(initialStepRef.current > 1);

  // Contact selection state
  const [allCustomerContacts, setAllCustomerContacts] = useState([]);
  const [contactDropdownValue, setContactDropdownValue] = useState('');
  const [showContactModal, setShowContactModal] = useState(false);
  // Contacts with the "Statement" tick cleared — those the run will skip.
  const [showExclusions, setShowExclusions] = useState(false);
  const [exclusions, setExclusions] = useState([]);
  const [loadingExclusions, setLoadingExclusions] = useState(false);
  const [viewingContactId, setViewingContactId] = useState(null);
  const [contactGroups, setContactGroups] = useState([]);
  const [loadingGroup, setLoadingGroup] = useState(false);

  // Wrapper to save state when step changes
  const setStep = (newStep) => {
    setStepState(newStep);
    // Where the run has got to, for whoever opens it next.
    pushWork({ step: newStep });
  };

  const setRunId = (newRunId) => {
    setRunIdState(newRunId);
  };

  const handleDateChange = (field, value) => {
    const next = { ...dates, [field]: value };
    setDates(next);
    // A draft run (handed over before generating) just keeps its new dates.
    if (workReady && work?.isDraft) {
      if (datesComplete(next)) pushWork({ dates: next });
      return;
    }
    // Reset all steps when dates change
    resetStepsFrom(2);
  };

  const [loading, setLoading] = useState(false);
  const [statements, setStatements] = useState([]);
  // Statements whose invoices are NOT bundled (kept on the run, so it goes
  // across when the run is handed over). Everything else is bundled.
  const [excludedInvoiceIds, setExcludedInvoiceIds] = useState(new Set());
  const includeInvoiceIds = useMemo(
    () => includedStatementIds(statements, excludedInvoiceIds),
    [statements, excludedInvoiceIds]
  );
  const [syncProgress, setSyncProgress] = useState(0);
  const [syncMessage, setSyncMessage] = useState('');
  const [emailResult, setEmailResult] = useState(null);
  // This run's customers sent to the printer and not confirmed, from the
  // delivery log (GET /api/statements/print-unconfirmed) — the step-3 banner
  // when nothing on this visit has printed yet.
  const [unconfirmedPrint, setUnconfirmedPrint] = useState(null);
  const [additionalPDFs, setAdditionalPDFs] = useState([]);
  // Where a send goes: 'groups' (by contact group — Email Invoices emailed,
  // Postal Invoices printed) | 'customers' | 'print' | 'office' | 'save'.
  const [emailDestination, setEmailDestination] = useState('groups');
  const [templateMode, setTemplateMode] = useState('email');
  const [include, setInclude] = useState(CONTENT_DEFAULTS.include);   // 'both' | 'statement' | 'invoices'
  const [suppressOverdueStamp, setSuppressOverdueStamp] = useState(CONTENT_DEFAULTS.suppressOverdueStamp);
  const [includeAllUnpaidInvoices, setIncludeAllUnpaidInvoices] = useState(CONTENT_DEFAULTS.includeAllUnpaidInvoices);
  // Fully-used credit notes listed on the statement / attached (Contents).
  const [listUsedCredits, setListUsedCredits] = useState(CONTENT_DEFAULTS.listUsedCredits);
  const [attachUsedCredits, setAttachUsedCredits] = useState(CONTENT_DEFAULTS.attachUsedCredits);
  // A send by contact group: where customers in neither group go.
  const [routeChoices, setRouteChoices] = useState({});
  // Review: which statements are shown, and the preview's layout.
  const [reviewFilter, setReviewFilter] = useState('all');
  const [previewTemplate, setPreviewTemplate] = useState('email');
  // The checks window, open at a section ('flags' | 'sync' | 'check' | 'accounts' | 'figures'), or null.
  const [checksFocus, setChecksFocus] = useState(null);
  const [flagsCount, setFlagsCount] = useState(0);
  // "Your statement runs" (opened from the header).
  const [showMyRuns, setShowMyRuns] = useState(false);
  // Whether an interim run can be made (migration statement_run_kind.sql).
  const [runKindReady, setRunKindReady] = useState(null);
  const [customEmail, setCustomEmail] = useState(user?.email || '');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  // customSyncDates removed — sync step eliminated
  const [statementsWithoutEmail, setStatementsWithoutEmail] = useState([]);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteStatementId, setNoteStatementId] = useState(null);
  // The office note needs statement_office_notes.sql (the list says).
  const [officeNotes, setOfficeNotes] = useState({ ready: true, reason: null });
  // showCustomSyncDialog removed — sync step eliminated

  // Pagination states for statements
  const [, setStatementPage] = useState(1);
  const [statementSortField, setStatementSortField] = useState('accountNo');
  const [statementSortDirection, setStatementSortDirection] = useState('asc');
  const [alertDialog, setAlertDialog] = useState({ isOpen: false, message: '', type: 'info' });
  // The run's "payments as at" stamp (statement_runs.metadata.figuresAsAt).
  const [figuresAsAt, setFiguresAsAt] = useState(null);
  // A print or email the server refused because the figures are over two
  // hours old: { message, args } — confirming resends it with
  // confirmStalePayments, which the server writes to Activity.
  const [staleConfirm, setStaleConfirm] = useState(null);

  // (Persistence is handled by usePersistedState above — each slot writes to
  // the nav store automatically on change.)

  // Rehydrate the statements list and the "include invoices" set when the
  // workflow is resumed from localStorage. Without this the user lands on
  // step 3 with statements=[] and includeInvoiceIds=new Set() — and because
  // the server interprets an empty Set as "include nothing for any
  // customer", a subsequent print/email would send statements only and
  // silently drop every customer's invoice bundle.
  //
  // Gated by a ref so we only fetch on the initial restore: a fresh
  // generate within the same session updates statements + includeInvoiceIds
  // directly and doesn't need this re-fetch.
  const didRehydrateRef = useRef(false);
  // The statement Xero-check (sync, the run's check, its gates and windows).
  // Its reload and approve are read through refs, so they may be defined below.
  const reloadForCheckRef = useRef(null);
  const approveForCheckRef = useRef(null);
  const xeroCheck = useStatementXeroCheck({
    runId, dates, selectedContacts, user,
    reload: () => (reloadForCheckRef.current ? reloadForCheckRef.current() : null),
    approve: (...args) => (approveForCheckRef.current ? approveForCheckRef.current(...args) : null)
  });
  const applyCheckList = xeroCheck.applyList;
  // The run's statements (approvals, notes) from the server. Used on resume,
  // when a run is opened from a hand-over, and when a colleague has changed
  // something on the run.
  const loadStatements = useCallback(async (forRunId) => {
    if (!forRunId) return null;
    try {
      const res = await fetch(`${API_URL}/api/statements/list/${forRunId}`, { credentials: 'include' });
      if (!res.ok) return null;
      const data = await res.json();
      if (String(runIdRef.current) !== String(forRunId)) return null;
      setStatements(data.statements || []);
      setFiguresAsAt(data.figuresAsAt || null);
      if (data.runKind) setRunKindState(normaliseRunKind(data.runKind));
      if (typeof data.runKindReady === 'boolean') setRunKindReady(data.runKindReady);
      if (typeof data.officeNoteReady === 'boolean') setOfficeNotes({ ready: data.officeNoteReady, reason: data.officeNoteReason || null });
      applyCheckList(data);
      return data.statements || [];
    } catch (e) {
      console.error('Failed to load the statement run:', e);
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyCheckList]);
  reloadForCheckRef.current = () => loadStatements(runIdRef.current);
  useEffect(() => {
    if (didRehydrateRef.current) return;
    if (!runId) return;
    if (statements.length > 0) {
      didRehydrateRef.current = true;
      return;
    }
    didRehydrateRef.current = true;
    // Which invoices are bundled is no longer reset here: it is kept on the
    // run (excludedInvoices) and applied when the run's progress loads —
    // every statement is bundled unless left out.
    loadStatements(runId);
  }, [runId, statements.length, loadStatements]);

  // Function to reset data for future steps
  const resetStepsFrom = (fromStep) => {
    switch(fromStep) {
      case 1: // Reset everything
        // A draft (handed over before generating) is left; once nobody is on
        // it the server closes it, so it does not linger in anyone's runs.
        if (runId && work?.ready && work.isDraft && Number(work.runId) === Number(runId)) {
          fetch(`${API_URL}/api/statements/work/${runId}/leave`, { method: 'POST', credentials: 'include' }).catch(() => {});
        }
        touchedRef.current = new Set();
        xeroCheck.reset();
        setSelectedContacts([]);
        setRunId(null);
        setContinuesRunId(null);
        setStatements([]);
        setFiguresAsAt(null);
        setExcludedInvoiceIds(new Set());
        setEmailResult(null);
        setUnconfirmedPrint(null);
        setSyncProgress(0);
        setSyncMessage('');
        clearWorkflowState();
        break;
      case 2: // Reset from step 2 onwards (generate & approve)
        // Generating again continues this run: whoever is on it moves along
        // to the new one (a draft keeps its id — its dates are just saved).
        if (runId) setContinuesRunId(runId);
        setRunId(null);
        setStatements([]);
        setFiguresAsAt(null);
        setEmailResult(null);
        setUnconfirmedPrint(null);
        xeroCheck.reset();
        break;
      case 3: // Reset step 3 (send/save/print)
        setEmailResult(null);
        break;
      default:
        break;
    }
  };

  // Confirm-and-reset for the header "Reset workflow" button. Distinct from
  // the resume prompt's "Start Fresh" path so we can show a different message
  // (and so the button is available outside the resume scenario too).
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const handleResetWorkflow = () => {
    resetStepsFrom(1);
    setStepState(1);
    setDates(getPreviousMonthRange());
    setShowResetConfirm(false);
  };

  // ── The run's progress, kept on the run (hand over / shared runs) ──────────
  // What this screen shows is applied from the run when it loads and whenever
  // a colleague changes it; what the person does here is saved to the run.
  // Before the server's migration has run, workReady is false and none of
  // this does anything.

  /** Put the run's saved progress on screen. `withStep` when opening it. */
  // `skip`: keys chosen on this screen since it opened the run, which the
  // run's first load must not overwrite (their save is on its way).
  const applyWorkValues = useCallback((w, { withStep = false, skip = null } = {}) => {
    if (!w?.ready || w.missing) return;
    const v = w.values || {};
    const take = (key) => !(skip && skip.has(key));
    if (w.isDraft && v.dates && take('dates')) setDates(v.dates);
    else if (!w.isDraft && w.period?.start && w.period?.end) setDates({ start: w.period.start, end: w.period.end });
    if (Array.isArray(v.contacts) && take('contacts')) setSelectedContacts(v.contacts);
    if ('excludedInvoices' in v && take('excludedInvoices')) setExcludedInvoiceIds(new Set(v.excludedInvoices || []));
    if (v.templateMode && take('templateMode')) setTemplateMode(v.templateMode);
    if (v.include && take('include')) setInclude(v.include);
    if (v.destination && take('destination')) setEmailDestination(v.destination);
    if (typeof v.suppressOverdueStamp === 'boolean' && take('suppressOverdueStamp')) setSuppressOverdueStamp(v.suppressOverdueStamp);
    if (typeof v.includeAllUnpaidInvoices === 'boolean' && take('includeAllUnpaidInvoices')) setIncludeAllUnpaidInvoices(v.includeAllUnpaidInvoices);
    if (typeof v.listUsedCredits === 'boolean' && take('listUsedCredits')) setListUsedCredits(v.listUsedCredits);
    if (typeof v.attachUsedCredits === 'boolean' && take('attachUsedCredits')) setAttachUsedCredits(v.attachUsedCredits);
    if (v.routeChoices && typeof v.routeChoices === 'object' && take('routeChoices')) setRouteChoices(v.routeChoices);
    // A generated run's kind is the run's own; a draft carries the choice.
    if (!w.isDraft && w.runKind) setRunKindState(normaliseRunKind(w.runKind));
    else if (w.isDraft && v.runKind && take('runKind')) setRunKindState(normaliseRunKind(v.runKind));
    if ('officeEmail' in v && take('officeEmail')) setCustomEmail(v.officeEmail || user?.email || '');
    if (withStep && v.step && take('step')) setStepState(Number(v.step));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  // Set when a run is opened on purpose (a hand-over, Your statement runs, a
  // replacement run): the screen then goes to the step the run is at.
  const openWithStepRef = useRef(false);
  const appliedVersionRef = useRef(null);
  const statementsStampRef = useRef(null);
  // What this screen has chosen since it opened the run (see applyWorkValues).
  const touchedRef = useRef(new Set());

  /** Save a change to the run; a refusal is shown, and the screen takes the run as it is. */
  const pushWork = useCallback(async (changes) => {
    // The hook does nothing until the run is known to be ready on the server.
    if (!runIdRef.current) return null;
    for (const key of Object.keys(changes || {})) touchedRef.current.add(key);
    const result = await runWork.save(changes, { runId: runIdRef.current });
    if (!result || result.ok) return result;
    if (result.code === 'RUN_CHANGED' && result.work) {
      applyWorkValues(result.work);
      setAlertDialog({ isOpen: true, type: 'warning', title: 'Changed by a colleague', message: result.error });
    } else if (result.code !== 'NOT_ON_RUN' && result.code !== 'RUN_SUPERSEDED') {
      // Not on the run / replaced: the effects below say so and move on.
      setAlertDialog({ isOpen: true, type: 'error', message: `Could not save this to the run: ${result.error}` });
    }
    return result;
  }, [runWork, applyWorkValues]);

  // The run loaded, or a colleague changed it: show theirs. Own saves come
  // back as the new version too, and are already on screen.
  useEffect(() => {
    if (!work?.ready || work.missing || Number(work.runId) !== Number(runId)) return;
    const key = `${work.runId}:${work.version}`;
    if (appliedVersionRef.current === key) return;
    const first = !appliedVersionRef.current || !appliedVersionRef.current.startsWith(`${work.runId}:`);
    appliedVersionRef.current = key;
    if (first || (work.lastChange?.by && work.lastChange.by !== meId)) {
      applyWorkValues(work, { withStep: openWithStepRef.current, skip: first ? touchedRef.current : null });
    }
    openWithStepRef.current = false;
  }, [work, runId, meId, applyWorkValues]);

  // Someone approved, unapproved, changed a note or sent statements on this
  // run: read its statements again.
  useEffect(() => {
    if (!work?.ready || work.missing || Number(work.runId) !== Number(runId)) return;
    const key = `${work.runId}:${work.statementsStamp}`;
    const prev = statementsStampRef.current;
    statementsStampRef.current = key;
    if (prev && prev !== key && prev.startsWith(`${work.runId}:`)) loadStatements(work.runId);
  }, [work, runId, loadStatements]);

  // Your statement runs, and the hand-overs not yet seen (the notices).
  const [mine, setMine] = useState({ loaded: false, ready: false, runs: [], notices: [], handedAway: [] });
  const loadMine = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/statements/work/mine`, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      if (data && Array.isArray(data.runs)) setMine({ loaded: true, ready: !!data.ready, runs: data.runs, notices: data.notices || [], handedAway: data.handedAway || [] });
      if (data && typeof data.runKindReady === 'boolean') setRunKindReady(data.runKindReady);
    } catch (_) { /* notices only */ }
  }, []);
  useEffect(() => {
    loadMine();
    const timer = setInterval(loadMine, 60000);
    return () => clearInterval(timer);
  }, [loadMine]);

  /** Open a run on this screen — one handed over, from Your statement runs, or a replacement. */
  const openRun = useCallback(async (target, { quiet = false } = {}) => {
    const id = Number(target?.runId ?? target);
    if (!id) return;
    try {
      await fetch(`${API_URL}/api/statements/work/${id}/seen`, { method: 'POST', credentials: 'include' });
    } catch (_) { /* the notice only */ }
    setEmailResult(null);
    setUnconfirmedPrint(null);
    setFiguresAsAt(null);
    setStatements([]);
    setExcludedInvoiceIds(new Set());
    setContinuesRunId(null);
    setStatementPage(1);
    didRehydrateRef.current = true;
    appliedVersionRef.current = null;
    statementsStampRef.current = null;
    touchedRef.current = new Set();
    openWithStepRef.current = true;
    runIdRef.current = id;
    setRunId(id);
    loadStatements(id);
    loadMine();
    if (!quiet) setShowResumePrompt(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadStatements, loadMine]);

  // Generated again by someone on the run: follow on to the new run.
  useEffect(() => {
    if (!work?.ready || Number(work.runId) !== Number(runId) || !work.supersededBy) return;
    const next = work.supersededBy;
    openRun(next, { quiet: true });
    setAlertDialog({
      isOpen: true, type: 'info', title: 'Generated again',
      message: `These statements have been generated again on the run, so this screen now shows the new run (${next}).`
    });
  }, [work, runId, openRun]);

  // No longer on the run (handed over from another tab or screen).
  useEffect(() => {
    if (!work?.ready || work.missing || Number(work.runId) !== Number(runId) || work.onRun !== false) return;
    if (printJobRef.current) return; // let a print being followed finish first
    const names = (work.people || []).map(p => p.name).join(', ');
    setAlertDialog({
      isOpen: true, type: 'info', title: 'Handed over',
      message: `This statement run has been handed over${names ? ` to ${names}` : ''} — it is no longer on your screen.`
    });
    resetStepsFrom(1);
    setStepState(1);
    setDates(getPreviousMonthRange());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [work, runId]);

  const chooseDestination = (dest) => {
    setEmailDestination(dest);
    pushWork({ destination: dest });
  };

  // Month-end or interim. Once generated, a change of kind starts the run
  // again (as a change of dates does).
  const chooseRunKind = (kind) => {
    const k = normaliseRunKind(kind);
    if (k === runKind) return;
    setRunKindState(k);
    if (workReady && work?.isDraft) { pushWork({ runKind: k }); return; }
    if (runId) resetStepsFrom(2);
  };

  // A Contents choice: on screen at once, kept with the run.
  const CONTENT_SETTERS = {
    include: setInclude,
    includeAllUnpaidInvoices: setIncludeAllUnpaidInvoices,
    suppressOverdueStamp: setSuppressOverdueStamp,
    listUsedCredits: setListUsedCredits,
    attachUsedCredits: setAttachUsedCredits
  };
  const changeContent = (key, value) => {
    const set = CONTENT_SETTERS[key];
    if (!set) return;
    set(value);
    pushWork({ [key]: value });
  };

  const chooseRoute = (statementId, choice) => {
    setRouteChoices(prev => {
      const next = { ...prev };
      if (choice) next[statementId] = choice; else delete next[statementId];
      pushWork({ routeChoices: next });
      return next;
    });
  };

  // The office address is saved once typing pauses, and only once it is an
  // address. The person's own address is saved as "nobody's in particular",
  // so a colleague who picks the run up sees theirs.
  const officeEmailTimerRef = useRef(null);
  const flushOfficeEmailRef = useRef(null);
  const changeOfficeEmail = (value) => {
    setCustomEmail(value);
    clearTimeout(officeEmailTimerRef.current);
    const save = () => {
      flushOfficeEmailRef.current = null;
      const stored = officeEmailToStore(value, user?.email);
      if (stored !== undefined) return pushWork({ officeEmail: stored });
      return null;
    };
    flushOfficeEmailRef.current = save;
    officeEmailTimerRef.current = setTimeout(save, 700);
  };
  useEffect(() => () => clearTimeout(officeEmailTimerRef.current), []);

  // Clear loading bar once email result is available
  useEffect(() => {
    if (emailResult) {
      setLoading(false);
      setSyncProgress(0);
      setSyncMessage('');
    }
  }, [emailResult]);

  // Fetch all customer contacts and contact groups for dropdown
  useEffect(() => {
    const fetchCustomerContacts = async () => {
      try {
        const queryParams = new URLSearchParams({
          contactStatus: 'ACTIVE',
          accountType: 'CUSTOMER',
          limit: '10000'
        });
        const response = await fetch(`${API_URL}/api/contacts/search?${queryParams}`, {
          credentials: 'include'
        });
        const data = await response.json();
        setAllCustomerContacts(data.contacts || []);
      } catch (error) {
        console.error('Failed to fetch customer contacts:', error);
      }
    };

    const fetchContactGroups = async () => {
      try {
        const response = await fetch(`${API_URL}/api/contacts/available-groups`, {
          credentials: 'include'
        });
        const data = await response.json();
        if (data.success) {
          setContactGroups(data.groups || []);
        }
      } catch (error) {
        console.error('Failed to fetch contact groups:', error);
      }
    };

    fetchCustomerContacts();
    fetchContactGroups();
  }, []);

  // Add all contacts from a group
  // Re-fetched on every open so an edit made from the list is reflected when
  // the user comes back to it.
  const openExclusions = async () => {
    setShowExclusions(true);
    setLoadingExclusions(true);
    try {
      const response = await fetch(`${API_URL}/api/contacts/no-statement`, { credentials: 'include' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to load');
      setExclusions(data.contacts || []);
    } catch (error) {
      console.error('Failed to load excluded contacts:', error);
      setAlertDialog({ isOpen: true, message: `Failed to load excluded contacts: ${error.message}`, type: 'error' });
    } finally {
      setLoadingExclusions(false);
    }
  };

  const addContactGroup = async (groupName) => {
    if (!groupName) return;
    setLoadingGroup(true);
    try {
      // Contacts with the "Statement" tick cleared are left out of a group
      // bulk-add — the flag means they don't receive statements at all.
      const response = await fetch(
        `${API_URL}/api/contacts/group/${encodeURIComponent(groupName)}/contacts?excludeNoStatement=true`,
        { credentials: 'include' }
      );
      const contacts = await response.json();
      if (Array.isArray(contacts)) {
        setSelectedContacts(prev => {
          const existingIds = new Set(prev.map(c => c.id));
          const newContacts = contacts.filter(c => !existingIds.has(c.id));
          return [...prev, ...newContacts];
        });
        pushWork({ contacts: { add: contacts } });
      }
    } catch (error) {
      console.error('Failed to fetch group contacts:', error);
      setAlertDialog({ isOpen: true, message: 'Failed to load group contacts', type: 'error' });
    } finally {
      setLoadingGroup(false);
    }
  };

  // Add contact to selection (from dropdown)
  const addContact = (contactId) => {
    if (!contactId) return;
    const contact = allCustomerContacts.find(c => c.id === contactId);
    if (contact && !selectedContacts.find(c => c.id === contactId)) {
      setSelectedContacts(prev => [...prev, contact]);
      pushWork({ contacts: { add: [contact] } });
    }
    setContactDropdownValue('');
  };

  // Remove contact from selection
  const removeContact = (contactId) => {
    setSelectedContacts(prev => prev.filter(c => c.id !== contactId));
    pushWork({ contacts: { remove: [{ id: contactId }] } });
  };

  // View contact in modal
  const viewContact = (contactId) => {
    setViewingContactId(contactId);
    setShowContactModal(true);
  };


  // Sync data from Xero with optional custom dates
  // Generate statements for selected contacts
  const generateStatements = async () => {
    setLoading(true);
    try {
      const contactIds = selectedContacts.map(c => c.id);

      // A draft run (handed over before generating), or the run this screen
      // was on before its dates changed: the new run continues it, and the
      // people on it move along.
      const continues = (workReady && work?.isDraft ? runId : null) || continuesRunId || null;

      // Step 1: Prepare invoices for selected contacts (creates run)
      const prepareResponse = await fetch(`${API_URL}/api/invoices/prepare-for-contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          start: dates.start,
          end: dates.end,
          contactIds,
          runKind,
          ...(continues ? { continuesRunId: continues } : {})
        }),
        credentials: 'include'
      });

      const prepareData = await prepareResponse.json();
      // A colleague on the same run has already generated it again: open theirs.
      if (prepareResponse.status === 409 && prepareData.code === 'RUN_SUPERSEDED' && prepareData.supersededBy) {
        openRun(prepareData.supersededBy, { quiet: true });
        setAlertDialog({ isOpen: true, type: 'info', message: prepareData.error });
        return;
      }
      if (!prepareData.success) {
        const kindRefused = prepareData.code === 'RUN_KIND_INVALID' || prepareData.code === 'RUN_KIND_NOT_READY';
        setAlertDialog({
          isOpen: true,
          title: kindRefused ? 'Not an interim month' : undefined,
          message: kindRefused ? prepareData.error : `Failed to prepare invoices: ${prepareData.error}`,
          type: kindRefused ? 'warning' : 'error'
        });
        return false;
      }

      const newRunId = prepareData.runId;
      setContinuesRunId(null);
      appliedVersionRef.current = `${newRunId}:seeded`;
      runIdRef.current = newRunId;
      setRunId(newRunId);

      // Step 2: Auto-approve all invoices for this run (for the selected contacts)
      await fetch(`${API_URL}/api/invoices/bulk-approve-all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ runId: newRunId, action: 'approve', contactIds }),
        credentials: 'include'
      });

      // Step 3: Generate statements for selected contacts
      const statementsResponse = await fetch(`${API_URL}/api/statements/generate-for-contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ runId: newRunId, contactIds }),
        credentials: 'include'
      });

      const statementsData = await statementsResponse.json();
      if (statementsData.success) {
        const stmts = statementsData.statements || [];
        setStatements(stmts);
        // The server checks the new run against Xero straight away: follow
        // it, and read the list (which carries each statement's check).
        xeroCheck.check.follow(statementsData.integrityCheck);
        loadStatements(newRunId);
        setExcludedInvoiceIds(new Set());
        // Everything chosen so far onto the new run, so whoever opens it —
        // now or after a hand-over — sees it.
        // Generated: on to Contents (step 2).
        setStepState(2);
        pushWork({
          step: 2,
          contacts: { set: selectedContacts },
          excludedInvoices: { set: [] },
          templateMode, include, destination: emailDestination,
          suppressOverdueStamp, includeAllUnpaidInvoices, listUsedCredits, attachUsedCredits, runKind
        });
        const stamp = statementsData.figuresAsAt || null;
        setFiguresAsAt(stamp);
        // Say so when the count is short because of the "Statement" tick,
        // rather than leaving the user to wonder where they went.
        const skipped = statementsData.skippedNoStatement || [];
        const skippedNote = skipped.length
          ? ` (${skipped.length} skipped — marked as not receiving statements: ${skipped.map(s => s.company_name).join(', ')})`
          : '';
        // Made from the figures held when Xero could not be reached: say how old.
        const figuresNote = stamp && !stamp.refreshedFromXero
          ? ` Xero could not be reached, so payments are as at ${asAtText(stamp.paymentsAsAt)}.`
          : '';
        setAlertDialog({
          isOpen: true,
          message: `Generated ${statementsData.count} statements${skippedNote}.${figuresNote}`,
          type: figuresNote ? 'warning' : 'success'
        });
        setStatementPage(1);
        return true;
      } else {
        setAlertDialog({ isOpen: true, message: `Failed to generate statements: ${statementsData.error || statementsResponse.statusText}`, type: 'error' });
      }
    } catch (error) {
      setAlertDialog({ isOpen: true, message: `Failed: ${error.message}`, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Update statement approval status
  // Sent with the status the screen showed: if a colleague on the run has
  // changed it since, the server refuses (409 STATEMENT_CHANGED) and the row
  // shows theirs instead of being silently overwritten.
  // A statement the Xero check found wrong asks "approve anyway?" first; the
  // yes goes as acknowledgeMismatch, which the server records.
  const updateApprovalStatus = async (id, status, { acknowledgeMismatch = false } = {}) => {
    const row = statements.find(s => s.id === id);
    const shown = row?.approval_status || 'unchecked';
    if (status === 'approved' && !acknowledgeMismatch && xeroCheck.askBeforeApprove(row)) return false;
    try {
      const response = await fetch(`${API_URL}/api/statements/approve/${id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, expected_status: shown, ...(acknowledgeMismatch ? { acknowledgeMismatch: true } : {}) }),
        credentials: 'include'
      });
      const data = await response.json().catch(() => ({}));
      if (xeroCheck.handleGate(response.status, data, (more) => updateApprovalStatus(id, status, more), row)) return false;

      if (response.ok) {
        if (acknowledgeMismatch) loadStatements(runIdRef.current);
        setStatements(prev => prev.map(stmt =>
          stmt.id === id
            ? { ...stmt, approval_status: status, approved_by_name: data.approved_by_name || stmt.approved_by_name, approved_at: data.approved_at || stmt.approved_at }
            : stmt
        ));
        return true;
      } else if (response.status === 409 && data.code === 'STATEMENT_CHANGED' && data.current) {
        setStatements(prev => prev.map(stmt => (stmt.id === id ? { ...stmt, ...data.current } : stmt)));
        setAlertDialog({
          isOpen: true, type: 'warning', title: 'Changed by a colleague',
          message: `${data.error} The screen now shows that — change it again if it still needs changing.`
        });
      } else {
        setAlertDialog({ isOpen: true, message: `Failed to update approval: ${data.error || response.statusText}`, type: 'error' });
      }
    } catch (error) {
      console.error('Approval update error:', error);
      setAlertDialog({ isOpen: true, message: `Failed to connect to server: ${error.message}`, type: 'error' });
    }
  };
  approveForCheckRef.current = updateApprovalStatus;

  // Bulk statement approval. Statements the Xero check found wrong are left
  // out by the server (approved one at a time, with the question); a check
  // still running is waited for unless the person goes ahead.
  const bulkApproval = async (action, { proceedWithoutCheck = false } = {}) => {
    try {
      let endpoint, body;

      if (action.includes('unchecked')) {
        endpoint = '/api/statements/bulk-approve-unchecked';
        body = {
          runId,
          action: action.includes('unapprove') ? 'unapprove' : 'approve'
        };
      } else {
        endpoint = '/api/statements/bulk-approve-all';
        body = {
          runId,
          action: action.includes('unapprove') ? 'unapprove' : 'approve'
        };
      }
      if (proceedWithoutCheck) body.proceedWithoutCheck = true;

      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        credentials: 'include'
      });

      const data = await response.json().catch(() => ({}));
      if (xeroCheck.handleGate(response.status, data, (more) => bulkApproval(action, more))) return;
      if (!response.ok) {
        throw new Error(data.error || 'Bulk approval request failed');
      }

      if (data.success) {
        setAlertDialog({ isOpen: true, ...bulkApprovalText(data) });

        // Refresh statements
        if (runId) {
          const statementsResponse = await fetch(`${API_URL}/api/statements/list/${runId}`, {
            credentials: 'include'
          });

          if (!statementsResponse.ok) {
            throw new Error('Failed to refresh statements');
          }

          const statementsData = await statementsResponse.json();

          if (statementsData.statements && statementsData.statements.length > 0) {
            setStatements(statementsData.statements);
            applyCheckList(statementsData);
          } else {
            throw new Error('No statements returned from server');
          }
        }
      } else {
        throw new Error(data.error || 'Bulk approval failed');
      }
    } catch (error) {
      console.error('Bulk approval error:', error);
      setAlertDialog({ isOpen: true, message: `Failed: ${error.message}`, type: 'error' });
    }
  };

  // Bulk approval for a list of statements (Review's "the N shown").
  const bulkApprovalIds = async (pageIds, action, { proceedWithoutCheck = false, scope = 'shown' } = {}) => {
    try {

      const response = await fetch(`${API_URL}/api/statements/bulk-approve-ids`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ids: pageIds,
          action: action === 'approve' ? 'approve' : 'unapprove',
          ...(proceedWithoutCheck ? { proceedWithoutCheck: true } : {})
        }),
        credentials: 'include'
      });

      const data = await response.json().catch(() => ({}));
      if (xeroCheck.handleGate(response.status, data, (more) => bulkApprovalIds(pageIds, action, { scope, ...more }))) return;
      if (!response.ok) {
        throw new Error(data.error || 'Bulk approval request failed');
      }

      if (data.success) {
        setAlertDialog({ isOpen: true, ...bulkApprovalText(data, ` of the ${pageIds.length} ${scope === 'selected' ? 'selected' : 'shown'}`) });

        const newStatus = action === 'approve' ? 'approved' : 'unapproved';
        const skipped = new Set((data.skippedMismatch || []).map(x => x.id));
        setStatements(prev => prev.map(stmt =>
          pageIds.includes(stmt.id) && !skipped.has(stmt.id) ? { ...stmt, approval_status: newStatus } : stmt
        ));
      } else {
        throw new Error(data.error || 'Bulk approval failed');
      }
    } catch (error) {
      console.error('Bulk page approval error:', error);
      setAlertDialog({ isOpen: true, message: `Failed: ${error.message}`, type: 'error' });
    }
  };


  // The note window: a statement's office note and its note for the customer
  // (StatementNoteDialog saves each, guarded against a colleague's change).
  const handleAddNote = (statementId) => {
    setNoteStatementId(statementId);
    setShowNoteModal(true);
  };
  const closeNoteDialog = () => {
    setShowNoteModal(false);
    setNoteStatementId(null);
  };
  const applyNoteSaved = (id, patch) => {
    setStatements(prev => prev.map(stmt => (stmt.id === id ? { ...stmt, ...patch } : stmt)));
  };

  // Check for statements without email addresses
  const checkEmailAddresses = async () => {
    try {
      const approvedStatements = statements.filter(s => s.approval_status === 'approved');
      const missingEmails = approvedStatements.filter(s => !s.email || s.email.trim() === '');

      if (missingEmails.length > 0) {
        setStatementsWithoutEmail(missingEmails);
        return false;
      }

      setStatementsWithoutEmail([]);
      return true;
    } catch (error) {
      setAlertDialog({ isOpen: true, message: `Failed to check email addresses: ${error.message}`, type: 'error' });
      return false;
    }
  };

  // Clear loading bar on user interaction
  const clearLoadingBar = () => {
    if (loading && syncProgress === 100) {
      setLoading(false);
      setSyncProgress(0);
      setSyncMessage('');
    }
  };

  // Verify password before sending to customers
  const verifyPassword = async (pwd) => {
    try {
      const response = await fetch(`${API_URL}/api/users/verify-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ password: pwd })
      });

      const data = await response.json();
      return data.success === true;
    } catch (error) {
      console.error('Password verification error:', error);
      return false;
    }
  };

  // Handle email/save initiation (check for password if sending to customers)
  const initiateEmailStatements = async () => {
    if (emailDestination === 'customers') {
      const hasAllEmails = await checkEmailAddresses();
      if (!hasAllEmails) {
        return;
      }
      setShowPasswordModal(true);
    } else if (emailDestination === 'groups' && routingPlan(statements, routeChoices).emailIds.length) {
      // A group send that emails customers asks for the password, as emailing customers always has.
      setShowPasswordModal(true);
    } else {
      await runSend();
    }
  };

  // Handle password confirmation
  const handlePasswordConfirm = async () => {
    const isValid = await verifyPassword(password);
    if (!isValid) {
      setAlertDialog({ isOpen: true, message: 'Incorrect password. Please try again.', type: 'error' });
      return;
    }

    setShowPasswordModal(false);
    setPassword('');
    await runSend();
  };

  // The print summary dialog: what printed, and — when the printer did not
  // confirm everything — who is missing, with "Reprint these".
  const openPrintSummary = (data) => {
    const ok = !!data?.success;
    setAlertDialog({
      isOpen: true,
      title: ok ? 'Printed' : data?.fromLog ? 'Sent to the printer, not confirmed' : 'Printing stopped — not everything was confirmed',
      type: ok ? 'success' : 'warning',
      message: <StatementPrintSummary result={data} onReprint={ok ? null : startReprint} />
    });
  };

  const startReprint = (reprint, options, reprintRunId) => {
    setAlertDialog(prev => ({ ...prev, isOpen: false }));
    setEmailResult(null);
    handleEmailStatements({ reprint, reprintOptions: options, runIdOverride: reprintRunId });
  };

  // A job's progress stream, until it ends. Shared by a fresh start and by
  // picking a print back up. A dropped connection is retried — the stream
  // replays the job's state on reconnect — and given up on (`lost`) only when
  // the browser stops retrying or after several failures in a row. Leaving
  // the screen closes it (`detached`, which callers ignore).
  const streamRef = useRef(null);
  useEffect(() => () => { if (streamRef.current) streamRef.current.detach(); }, []);
  const watchJob = (jobId, destination) => new Promise((resolve, reject) => {
    const es = new EventSource(`${API_URL}/api/jobs/${jobId}/stream`, { withCredentials: true });
    const close = () => {
      es.close();
      if (streamRef.current && streamRef.current.es === es) streamRef.current = null;
    };
    streamRef.current = { es, detach: () => { close(); reject(Object.assign(new Error('detached'), { detached: true })); } };
    let dropped = 0;
    es.onmessage = (ev) => {
      dropped = 0;
      let payload;
      try { payload = JSON.parse(ev.data); } catch (_) { return; }
      if (payload.type === 'progress' && payload.progress) {
        const { current, total, message } = payload.progress;
        // Only update the bar once *real* progress is happening. The job's
        // initial replay (current=0,total=0) and the first server emit
        // (current=0,total=N) would otherwise drop the optimistic 5%
        // start to 0%, making the bar appear to disappear and reappear.
        if (current > 0 && total > 0) {
          setSyncProgress(Math.min(100, Math.round((current / total) * 100)));
        }
        if (message) setSyncMessage(message);
      } else if (payload.type === 'complete') {
        close();
        resolve({ result: payload.result, hasDownload: payload.hasDownload });
      } else if (payload.type === 'error') {
        close();
        reject(new Error(payload.error || 'Job failed'));
      }
      // 'heartbeat' events ignored
    };
    es.onerror = () => {
      dropped += 1;
      if (es.readyState === EventSource.CONNECTING && dropped <= 5) {
        setSyncMessage('Connection to the server dropped — reconnecting...');
        return;
      }
      close();
      reject(Object.assign(new Error(destination === 'print'
        ? 'Connection to the server was lost while printing.'
        : 'Connection to server lost'), { lost: true }));
    };
  });

  // 'pending' | 'running' | 'completed' | 'failed', or null when the server no
  // longer has the job (an hour after it ended, or after a restart).
  const jobStatus = async (jobId) => {
    try {
      const res = await fetch(`${API_URL}/api/jobs/${jobId}`, { credentials: 'include' });
      if (!res.ok) return null;
      return (await res.json()).status || null;
    } catch (_) {
      return null;
    }
  };

  // What the delivery log says this run still has to print. Sets the step-3
  // banner, and returns the list in the summary's shape (null when there is
  // nothing). A print of the run still going is followed instead.
  const loadUnconfirmedPrint = async (forRunId) => {
    if (!forRunId) return null;
    try {
      const res = await fetch(`${API_URL}/api/statements/print-unconfirmed/${forRunId}`, { credentials: 'include' });
      if (!res.ok) return null;
      const data = await res.json();
      if (data.active) {
        if (data.active.jobId && !printJobRef.current) followPrint(data.active.jobId, forRunId);
        return null;
      }
      const list = (data.unconfirmed || []).length ? { ...data, fromLog: true, success: false } : null;
      setUnconfirmedPrint(list);
      return list;
    } catch (err) {
      console.error('Unconfirmed print list failed:', err);
      return null;
    }
  };

  // What a finished print got onto paper: the step-3 banner and the summary.
  const finishPrint = (data) => {
    const ok = !!data.success;
    const missing = (data.unconfirmed || []).length;
    setSyncMessage(ok ? 'Complete!' : 'Printing stopped');
    setSyncProgress(100);
    setUnconfirmedPrint(null);
    setEmailResult({
      ...data,
      printProblem: !ok,
      message: ok
        ? printHeadline(data)
        : `${printHeadline(data)} ${missing} customer${missing !== 1 ? 's' : ''} not confirmed printed.`
    });
    openPrintSummary(data);
  };

  // Follow a statement print to its end — one just started, or one picked
  // back up after the screen was left — and show what the printer confirmed.
  const followPrint = async (jobId, jobRunId) => {
    setPrintJob({ id: jobId, runId: jobRunId });
    setLoading(true);
    setEmailResult(null);
    setUnconfirmedPrint(null);
    setSyncProgress(prev => prev || 5);
    try {
      const { result } = await watchJob(jobId, 'print');
      setPrintJob(null);
      finishPrint(result || {});
      return result || {};
    } catch (err) {
      if (err.detached) return null;
      const status = err.lost ? await jobStatus(jobId) : null;
      const stillRunning = status === 'pending' || status === 'running';
      if (!stillRunning) setPrintJob(null);
      setLoading(false);
      setSyncProgress(0);
      setSyncMessage('');
      if (stillRunning) {
        setAlertDialog({
          isOpen: true,
          type: 'warning',
          message: 'Lost touch with the print, but it is still running on the server. Leave this screen and come back (or reload the page) to follow it again.'
        });
        return null;
      }
      // Gone (the server restarted) or ended in an error: the delivery log
      // says who was sent and not confirmed.
      const fromLog = await loadUnconfirmedPrint(jobRunId);
      if (fromLog) {
        openPrintSummary({ ...fromLog, failure: err.lost ? 'The print stopped before it finished — the server may have restarted.' : err.message });
      } else {
        setAlertDialog({ isOpen: true, message: `Failed: ${err.message}`, type: 'error' });
      }
      return { success: false };
    }
  };

  // Back on the screen: pick up the print being followed (the stream replays
  // its end if it finished meanwhile), or — if the server no longer has it —
  // show what did not print. Once, on mount.
  const didResumePrintRef = useRef(false);
  useEffect(() => {
    if (didResumePrintRef.current) return;
    didResumePrintRef.current = true;
    const job = printJobRef.current;
    if (!job?.id) return;
    (async () => {
      if (await jobStatus(job.id)) {
        followPrint(job.id, job.runId);
        return;
      }
      setPrintJob(null);
      const fromLog = await loadUnconfirmedPrint(job.runId);
      if (fromLog) openPrintSummary({ ...fromLog, failure: 'The print ended while this screen was closed and its result is no longer on the server.' });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On step 3, what this run still has to print, from the delivery log.
  useEffect(() => {
    if (step !== 4 || !runId || printJobRef.current) return;
    loadUnconfirmedPrint(runId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, runId]);

  // Extra PDFs: kept with the run once its progress is kept on the server (so
  // a colleague's send has them too), else held in this tab as before.
  const runAttachments = workReady ? (work?.values?.attachments || []) : null;
  // Every send asks for the run's own PDFs (the server has none before its
  // migration, and only gives them to someone who may use the run) and sends
  // any held in this tab; the server sends each PDF once.
  const appendAttachments = (formData) => {
    if (runId) formData.append('runAttachments', 'true');
    additionalPDFs.forEach(file => formData.append('additionalPDFs', file));
  };
  const pdfKey = (f) => `${f.name}|${f.size}`;
  const shownAttachments = [...(runAttachments || []), ...additionalPDFs];
  const [attachmentBusy, setAttachmentBusy] = useState(false);
  const chooseAttachments = async (picked) => {
    // The same PDF (name + size) is kept once.
    const have = new Set(shownAttachments.map(pdfKey));
    const files = [];
    for (const f of picked) {
      if (have.has(pdfKey(f))) continue;
      have.add(pdfKey(f));
      files.push(f);
    }
    if (!files.length) return;
    if (!runAttachments) { setAdditionalPDFs(prev => [...prev, ...files]); return; }
    setAttachmentBusy(true);
    const result = await runWork.addAttachments(files);
    setAttachmentBusy(false);
    if (result && !result.ok) setAlertDialog({ isOpen: true, type: 'error', message: `Could not keep the PDFs with the run: ${result.error}` });
  };
  const removeAttachment = async (att) => {
    if (!att.id) { setAdditionalPDFs(prev => prev.filter(f => f !== att)); return; }
    const result = await runWork.removeAttachment(att.id);
    if (result && !result.ok) setAlertDialog({ isOpen: true, type: 'error', message: `Could not take the PDF off: ${result.error}` });
  };

  // Email or save statements.
  // `reprint` (from a print that was not fully confirmed) sends just the
  // listed statements' missing statement pages / invoices back to the
  // printer, with the options that print used.
  // `destinationOverride` / `statementIds` / `quiet`: one leg of a send by
  // contact group (emails, then the print) — only those statements, and no
  // success dialog of its own (runGroupSend says how the whole send went).
  // Returns { ok, sent, failed } (or nothing when it stopped to ask).
  const handleEmailStatements = async ({
    reprint = null, reprintOptions = null, runIdOverride = null, confirmStalePayments = false, proceedWithoutCheck = false,
    destinationOverride = null, statementIds = null, quiet = false
  } = {}) => {
    const forRunId = runIdOverride || runId;
    if (!forRunId) {
      setAlertDialog({ isOpen: true, message: 'No statements to send', type: 'error' });
      return null;
    }
    const destination = reprint ? 'print' : (destinationOverride || emailDestination);

    setLoading(true);
    setSyncProgress(5);
    setSyncMessage(reprint ? 'Preparing the reprint...' : 'Preparing statement/s...');

    try {
      const formData = new FormData();
      formData.append('runId', forRunId);
      // Customers are emailed with the email layout and printed with the
      // print layout; the office copy and the ZIP use the layout chosen.
      formData.append('templateMode', destination === 'customers' ? 'email' : destination === 'print' ? 'print' : templateMode);
      formData.append('include', include);
      formData.append('includeInvoiceIds', JSON.stringify([...includeInvoiceIds]));
      formData.append('suppressOverdueStamp', reprintOptions ? !!reprintOptions.suppressOverdueStamp : suppressOverdueStamp);
      formData.append('includeAllUnpaidInvoices', reprintOptions ? !!reprintOptions.includeAllUnpaidInvoices : includeAllUnpaidInvoices);
      formData.append('listUsedCredits', reprintOptions ? !!reprintOptions.listUsedCredits : listUsedCredits);
      formData.append('attachUsedCredits', reprintOptions ? !!reprintOptions.attachUsedCredits : attachUsedCredits);
      if (statementIds && !reprint) formData.append('statementIds', JSON.stringify(statementIds));
      if (reprint) formData.append('reprint', JSON.stringify(reprint));
      // The user has confirmed sending on figures over two hours old.
      if (confirmStalePayments) formData.append('confirmStalePayments', 'true');
      // The person chose to go ahead while the run's Xero check was running.
      if (proceedWithoutCheck) formData.append('proceedWithoutCheck', 'true');

      let endpoint;

      if (destination === 'save') {
        endpoint = `${API_URL}/api/statements/save-local`;
      } else if (destination === 'customers') {
        endpoint = `${API_URL}/api/statements/email-customers`;
        appendAttachments(formData);
      } else if (destination === 'print') {
        endpoint = `${API_URL}/api/statements/print`;
      } else {
        endpoint = `${API_URL}/api/statements/email-office`;
        formData.append('email', customEmail);
        appendAttachments(formData);
      }

      // Step 1: POST starts the job and returns a jobId immediately
      const startResponse = await fetch(endpoint, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      if (!startResponse.ok) {
        const errData = await startResponse.json().catch(() => ({ error: `HTTP ${startResponse.status}` }));
        // Payments over two hours old (Xero not reachable): ask, then resend
        // the same request confirmed. Nothing has been sent.
        if (startResponse.status === 409 && errData.code === 'PAYMENTS_STALE') {
          setLoading(false);
          setSyncProgress(0);
          setSyncMessage('');
          setStaleConfirm({
            message: errData.error || 'The payment figures are over two hours old.',
            args: { reprint, reprintOptions, runIdOverride, proceedWithoutCheck, destinationOverride, statementIds, quiet, group: groupSendRef.current }
          });
          return null;
        }
        // Emailing customers / printing wait for the Accounts confirmation
        // (a colleague may have unticked it since this screen looked). Nothing sent.
        if (startResponse.status === 409 && errData.code === 'ACCOUNTS_NOT_CONFIRMED') {
          setLoading(false);
          setSyncProgress(0);
          setSyncMessage('');
          runWork.reload();
          setAlertDialog({ isOpen: true, type: 'warning', title: 'Waiting for Accounts', message: errData.error });
          return null;
        }
        // The run's Xero check is still running: ask, then resend. Nothing sent.
        if (startResponse.status === 409 && errData.code === 'CHECK_IN_PROGRESS') {
          setLoading(false);
          setSyncProgress(0);
          setSyncMessage('');
          const group = groupSendRef.current;
          xeroCheck.handleGate(409, errData, (more) => (group
            ? runGroupSend({ ...group, ...more })
            : handleEmailStatements({ reprint, reprintOptions, runIdOverride, confirmStalePayments, destinationOverride, statementIds, quiet, ...more })));
          return null;
        }
        // A print already running on this printer or for this run: when it is
        // this user's own, follow it rather than start a second.
        if (startResponse.status === 409 && destination === 'print' && errData.jobId) {
          setSyncMessage('A print is already running — following it.');
          await followPrint(errData.jobId, errData.runId || Number(forRunId));
          return null;
        }
        throw new Error(errData.error || `Failed to start job (HTTP ${startResponse.status})`);
      }

      const { jobId } = await startResponse.json();
      if (!jobId) throw new Error('Server did not return a job ID');

      // A print runs for as long as the printer takes (each part is confirmed
      // by the printer), so it is followed in a way that survives the screen
      // being left: see followPrint.
      if (destination === 'print') {
        const printed = await followPrint(jobId, Number(forRunId));
        return { ok: !!printed?.success, printed };
      }

      // Step 2: Open SSE stream and wait for terminal event.
      const jobOutcome = await watchJob(jobId, destination);

      // Save-to-download path: fetch the produced ZIP via the job download endpoint
      if (destination === 'save' && jobOutcome.hasDownload) {
        const dlRes = await fetch(`${API_URL}/api/jobs/${jobId}/download`, { credentials: 'include' });
        if (!dlRes.ok) throw new Error(`Download failed (HTTP ${dlRes.status})`);
        const generated = parseInt(dlRes.headers.get('X-Generated-Count') || '0', 10);
        const failed = parseInt(dlRes.headers.get('X-Failed-Count') || '0', 10);
        const blob = await dlRes.blob();
        const disposition = dlRes.headers.get('Content-Disposition') || '';
        const nameMatch = disposition.match(/filename="?([^"]+)"?/);
        const downloadName = nameMatch ? nameMatch[1] : 'Statements.zip';
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = downloadName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);

        setSyncMessage('Complete!');
        setSyncProgress(100);
        setAlertDialog({
          isOpen: true,
          type: failed > 0 ? 'warning' : 'success',
          message: failed > 0
            ? `Downloaded ${generated} PDFs. ${failed} failed — see _failed.txt inside the ZIP.`
            : `Downloaded ${generated} PDF${generated !== 1 ? 's' : ''}.`
        });
        return { ok: failed === 0 };
      }

      // The rest of this function expects a `data` variable shaped like the
      // old POST response body. Map jobOutcome.result onto it.
      const data = jobOutcome.result || {};

      // Check if we have results to analyze
      const results = data.results || [];
      const successfulCount = results.filter(r => r.success).length;
      const failedCount = results.filter(r => !r.success).length;

      if (data.success) {
        // All succeeded
        setSyncMessage('Complete!');
        setSyncProgress(100);

        // Email/save responses include a `message` field from the server which
        // the step-3 success banner (next to the tick) renders.
        setEmailResult(data);

        const successMessage = destination === 'customers'
          ? `Emailed ${data.emailsSent} statements to customers`
          : `Emailed ${data.emailsSent} statements to ${customEmail}`;
        if (!quiet) setAlertDialog({ isOpen: true, message: successMessage, type: 'success' });
        return { ok: true, sent: data.emailsSent || successfulCount, failed: 0 };
      } else if (results.length > 0 && successfulCount > 0) {
        // Partial success - some sent, some failed
        setSyncMessage('Complete with errors');
        setSyncProgress(100);
        setEmailResult(data);

        const handleCopied = () => {
          setAlertDialog(prev => ({
            ...prev,
            message: (
              <EmailResultSummary
                results={results}
                onCopy={() => {
                  // Show brief "Copied!" feedback then close
                  setAlertDialog({ isOpen: true, message: 'Results copied to clipboard!', type: 'success' });
                }}
              />
            )
          }));
        };

        setAlertDialog({
          isOpen: true,
          title: 'Partial Success',
          message: (
            <EmailResultSummary results={results} onCopy={handleCopied} />
          ),
          type: 'warning'
        });
        return { ok: false, sent: successfulCount, failed: failedCount };
      } else if (results.length > 0 && failedCount === results.length) {
        // All failed
        setSyncMessage('Failed');
        setSyncProgress(0);
        setLoading(false);

        setAlertDialog({
          isOpen: true,
          title: 'All Emails Failed',
          message: (
            <EmailResultSummary results={results} onCopy={() => {
              setAlertDialog({ isOpen: true, message: 'Results copied to clipboard!', type: 'success' });
            }} />
          ),
          type: 'error'
        });
        return { ok: false, sent: 0, failed: failedCount };
      } else {
        // Generic error (no results array)
        setAlertDialog({
          isOpen: true,
          message: `Failed: ${data.error || data.message || 'Unknown error'}`,
          type: 'error'
        });
        setLoading(false);
        setSyncProgress(0);
        setSyncMessage('');
        return { ok: false };
      }

    } catch (error) {
      if (error.detached) return null; // the screen was left; nothing to show
      setAlertDialog({ isOpen: true, message: `Failed: ${error.message}`, type: 'error' });
      setLoading(false);
      setSyncProgress(0);
      setSyncMessage('');
      return { ok: false };
    }
  };

  // ── Send by contact group ──────────────────────────────────────────────
  // Email Invoices members emailed (email layout), then Postal Invoices
  // members printed (print layout); members of both get both (utils/
  // statementRunKind routingPlan). One send, each leg with its own progress.
  // groupSendRef holds the send while a question (stale figures, the Xero
  // check) is asked, so the answer resumes the whole send.
  const groupSendRef = useRef(null);
  const runGroupSend = async ({ confirmStalePayments = false, proceedWithoutCheck = false, skipEmail = false } = {}) => {
    const plan = routingPlan(statements, routeChoices);
    groupSendRef.current = { confirmStalePayments, proceedWithoutCheck, skipEmail };
    try {
      let emailed = null;
      if (plan.emailIds.length && !skipEmail) {
        setSyncMessage(`Emailing ${plan.emailIds.length} customer${plan.emailIds.length === 1 ? '' : 's'}…`);
        emailed = await handleEmailStatements({
          destinationOverride: 'customers', statementIds: plan.emailIds, quiet: true, confirmStalePayments, proceedWithoutCheck
        });
        if (!emailed) return; // stopped to ask, or the screen was left
        // The email leg is done either way; an answer to a question asked by the print does not send it again.
        groupSendRef.current = { confirmStalePayments, proceedWithoutCheck, skipEmail: true };
      }
      let printed = null;
      if (plan.printIds.length) {
        setEmailResult(null);
        printed = await handleEmailStatements({
          destinationOverride: 'print', statementIds: plan.printIds, quiet: true, confirmStalePayments, proceedWithoutCheck
        });
        if (!printed) return;
      }
      const parts = [];
      if (emailed) parts.push(`${emailed.sent || 0} emailed${emailed.failed ? `, ${emailed.failed} not` : ''}`);
      if (printed) parts.push(printed.ok ? `${plan.printIds.length} printed` : 'the print did not finish — see what didn\u2019t print');
      if (!printed || printed.ok) {
        setAlertDialog({
          isOpen: true,
          type: (emailed && !emailed.ok) || (printed && !printed.ok) ? 'warning' : 'success',
          title: 'Sent by contact group',
          message: parts.length ? `${parts.join('; ')}.` : 'Nothing to send.'
        });
      }
      groupSendRef.current = null;
    } catch (err) {
      groupSendRef.current = null;
      setAlertDialog({ isOpen: true, type: 'error', message: `Failed: ${err.message}` });
    }
  };

  /** The send button: the chosen destination (a group send asks for the password if it emails). */
  const runSend = async (extra = {}) => {
    if (emailDestination === 'groups') return runGroupSend(extra);
    groupSendRef.current = null;
    return handleEmailStatements(extra);
  };

  // ── Hand over ──────────────────────────────────────────────────────────
  const [showHandOver, setShowHandOver] = useState(false);
  // "Hand over to Accounts": the same window, in Accounts mode.
  const [handOverToAccounts, setHandOverToAccounts] = useState(false);
  const openHandOver = (toAccounts) => { setHandOverToAccounts(!!toAccounts); setShowHandOver(true); };
  // Opening a handed-over run when this screen has a run of its own: asked first.
  const [openConfirm, setOpenConfirm] = useState(null);

  // Is the server ready (migration run)? Unknown until it has answered.
  const serverReady = runId ? (work && Number(work.runId) === Number(runId) ? workReady : null) : (mine.loaded ? mine.ready : null);
  const handOverReason = serverReady === null
    ? 'Checking the run…'
    : handOverBlock({
      ready: serverReady,
      // A print being followed does not stop it: whoever takes the run on
      // can follow the same print (they are on the run).
      busy: loading && !printJob,
      hasRun: !!runId,
      onRun: work?.onRun,
      contactsCount: selectedContacts.length,
      datesOk: datesComplete(dates)
    });
  const handOverSubject = `the ${runId && work?.period?.label && !work.isDraft ? work.period.label : periodLabel(dates)} statements`;
  const handOverStage = stageText({ step, isDraft: !runId || work?.isDraft, statements: statements.length });

  // ── The Accounts step ──────────────────────────────────────────────────
  // The run's Accounts confirmation, as the server holds it for this run's
  // period end (null until the run has loaded, or before the migration).
  const accounts = runId && workReady && work && Number(work.runId) === Number(runId) ? (work.accounts || null) : null;
  const accountsPeriodEnd = accounts?.periodEnd || work?.period?.end || dates.end;
  const [accountsSaving, setAccountsSaving] = useState(false);
  const accountsReason = accountsToggleBlock({
    ready: serverReady,
    loaded: serverReady !== null,
    onRun: work?.onRun,
    busy: accountsSaving,
    isDraft: !runId || work?.isDraft
  });
  const accountsSendReason = accountsSendBlock({
    destination: emailDestination, ready: serverReady, loaded: serverReady !== null, accounts
  });
  const accountsHandOverReason = accountsHandOverBlock({ handOverReason, statementsCount: statements.length });
  const toggleAccounts = async (confirmed) => {
    setAccountsSaving(true);
    const r = await runWork.confirmAccounts(confirmed);
    setAccountsSaving(false);
    if (r && r.ok === false) {
      setAlertDialog({ isOpen: true, type: r.code === 'RUN_CHANGED' ? 'warning' : 'error', message: r.error });
    }
  };
  const accountsPanel = (where) => (
    <StatementAccountsPanel
      accounts={accounts}
      periodEnd={accountsPeriodEnd}
      disabledReason={accountsReason}
      onToggle={toggleAccounts}
      idSuffix={`-${where}`}
    />
  );

  /** Everything on this screen, as changes to the run — so nothing stays behind in this tab. */
  const snapshotChanges = () => {
    const c = {
      step,
      contacts: { set: selectedContacts },
      templateMode,
      include,
      destination: emailDestination,
      suppressOverdueStamp,
      includeAllUnpaidInvoices,
      listUsedCredits,
      attachUsedCredits,
      routeChoices,
      runKind
    };
    if (statements.length) c.excludedInvoices = { set: [...excludedInvoiceIds] };
    if (work?.isDraft && datesComplete(dates)) c.dates = dates;
    const office = officeEmailToStore(customEmail, user?.email);
    if (office !== undefined) c.officeEmail = office;
    return c;
  };

  const submitHandOver = async ({ toUserIds, message, keepMe, emailThem }) => {
    const toAccounts = handOverToAccounts;
    try {
      clearTimeout(officeEmailTimerRef.current);
      flushOfficeEmailRef.current = null;
      if (runId) {
        const snap = await pushWork(snapshotChanges());
        if (snap && snap.ok === false) {
          return { ok: false, error: `${snap.error} Check the screen, then hand it over again.` };
        }
      }
      const res = await fetch(`${API_URL}/api/statements/work/hand-over`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          runId: runId || null,
          ...(runId ? {} : { draft: { dates, contacts: selectedContacts } }),
          toUserIds, message, keepMe, emailThem, ...(toAccounts ? { toAccounts: true } : {})
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return { ok: false, error: data.error || `HTTP ${res.status}` };
      setShowHandOver(false);
      if (data.keptOn) {
        if (!runId && data.runId) {
          // Shared at step 1: this screen is now on the draft run.
          appliedVersionRef.current = `${data.runId}:seeded`;
          runIdRef.current = data.runId;
          setRunId(data.runId);
        } else if (data.work) {
          runWork.setWork(data.work);
        }
      } else {
        // It has gone to them: off this screen. A print of it carries on, and
        // they can follow it; this screen stops following.
        if (printJobRef.current) {
          if (streamRef.current) streamRef.current.detach();
          setPrintJob(null);
          setLoading(false);
          setSyncProgress(0);
          setSyncMessage('');
        }
        resetStepsFrom(1);
        setStepState(1);
        setDates(getPreviousMonthRange());
      }
      loadMine();
      setAlertDialog({
        isOpen: true,
        type: (data.emailFailed || []).length ? 'warning' : 'success',
        title: data.toAccounts ? 'Handed to Accounts' : (data.keptOn ? 'Shared' : 'Handed over'),
        message: handOverResultText(data)
      });
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err.message };
    }
  };

  const askToOpen = (target) => {
    const sameRun = runId && Number(runId) === Number(target.runId);
    if (sameRun) { openRun(target); return; }
    const ownProgress = runId || selectedContacts.length > 0 || step > 1;
    if (!ownProgress) { openRun(target); return; }
    const keeps = runId && workReady && work?.onRun !== false;
    setOpenConfirm({
      target,
      message: `Open ${target.unseen ? handOverNoticeText(target).replace(/ to you(?= for Accounts' check$|$)/, '') : (target.period?.label ? `the ${target.period.label} statements` : `statement run ${target.runId}`)} on this screen? `
        + (keeps
          ? 'The run on your screen now stays as it is — you can come back to it from Your statement runs.'
          : (runId ? 'The run on your screen now will be closed here.' : 'The contacts and dates chosen on this screen will be cleared.'))
    });
  };

  /** "Take it back" (a run handed over and not opened yet) or "Take over this run". */
  const takeRun = async (target, mode) => {
    try {
      const res = await fetch(`${API_URL}/api/statements/work/${target.runId}/take-over`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ mode })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setAlertDialog({ isOpen: true, type: 'error', message: data.error || `HTTP ${res.status}` });
        loadMine();
        return;
      }
      if (Number(target.runId) === Number(runId)) runWork.reload();
      else askToOpen({ ...target, unseen: false });
      loadMine();
      setAlertDialog({
        isOpen: true, type: 'success', title: mode === 'take_back' ? 'Taken back' : 'Taken over',
        message: mode === 'take_back'
          ? `The ${target.period?.label || ''} statements are yours again${(data.removed || []).length ? ` — ${data.removed.join(', ')} no longer ha${data.removed.length === 1 ? 's' : 've'} it` : ''}.`
          : 'You are now on this run. Anyone already on it stays on it.'
      });
    } catch (err) {
      setAlertDialog({ isOpen: true, type: 'error', message: err.message });
    }
  };

  // Opened from Statement Run History ("Take over this run"): open it here.
  useEffect(() => {
    const id = useNavStore.getState().pages['generateStatements.openRunId'];
    if (!id) return;
    useNavStore.getState().clearPage('generateStatements.openRunId');
    askToOpen({ runId: id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismissNotice = async (n) => {
    try {
      await fetch(`${API_URL}/api/statements/work/${n.runId}/seen`, { method: 'POST', credentials: 'include' });
    } catch (_) { /* notice only */ }
    loadMine();
  };

  // "Acc #" sorts in the order the server prints the run (printOrder) and
  // blanks sort last (utils/statementOrder.js). Statements the Xero check
  // found wrong come first, in red, whatever the sort.
  const statementOrder = compareStatements(statementSortField, statementSortDirection);
  const sortedStatements = [...statements]
    .sort((a, b) => (integrityRank(a) - integrityRank(b)) || statementOrder(a, b));
  const integritySummary = summariseIntegrity(statements);

  const allStatementsChecked = statements.length > 0 &&
    statements.every(stmt => stmt.approval_status !== 'unchecked');
  const toReview = statements.filter(s => (s.approval_status || 'unchecked') === 'unchecked').length;
  const approvedCount = statements.filter(s => s.approval_status === 'approved').length;

  // ── The four-step layout (Statement Runs Redesign, 20 Oct 2026) ────────────
  const interim = isInterim(runKind);
  const generated = !!runId && statements.length > 0;
  const kindReadyReason = runKindReady === false ? 'Not ready — migration statement_run_kind.sql has not run' : null;
  const kindBlock = runKindBlock({ kind: runKind, dates, today: londonToday(), ready: runKindReady !== false });
  const content = { include, includeAllUnpaidInvoices, suppressOverdueStamp, listUsedCredits, attachUsedCredits };
  const plan = routingPlan(statements, routeChoices);
  const sendingToCustomers = emailDestination === 'customers' || emailDestination === 'print' || emailDestination === 'groups';

  // Month pills: an interim run re-issues a month that has ended.
  const presetOffsets = interim ? [-3, -2, -1] : [-2, -1, 0];
  const presets = presetOffsets.map(offset => {
    const range = getMonthRange(offset);
    return { label: getMonthNameShort(offset), range, active: dates.start === range.start && dates.end === range.end };
  });
  const choosePreset = (range) => {
    setDates(range);
    if (workReady && work?.isDraft) { pushWork({ dates: range }); return; }
    if (runId) resetStepsFrom(2);
  };

  const periodEndLabel = dates.end ? new Date(`${dates.end}T00:00:00Z`).toLocaleDateString('en-GB', { timeZone: 'UTC', day: 'numeric', month: 'short', year: 'numeric' }) : undefined;

  // The Xero check's state in a line (the chip and the checks window).
  const checkText = !xeroCheck.meta.integrityAvailable
    ? (xeroCheck.meta.integrityReason || 'Not ready — migration statement_reconciliation.sql has not run')
    : xeroCheck.check.live
      ? (xeroCheck.check.progress?.message || 'Checking the statements against Xero…')
      : integritySummary.checked === 0
        ? 'Not checked against Xero yet'
        : integritySummary.mismatchUnacknowledged
          ? `${integritySummary.mismatchUnacknowledged} statement${integritySummary.mismatchUnacknowledged === 1 ? ' doesn’t' : 's don’t'} match — first in the list, in red. Open its Xero check to see why.`
          : `Checked: ${integritySummary.match} match${integritySummary.advisory ? `, ${integritySummary.advisory} with a note` : ''}${integritySummary.error ? `, ${integritySummary.error} not fully checked` : ''}${integritySummary.mismatch ? `, ${integritySummary.mismatch} approved anyway` : ''}`;

  const flagsHide = interim ? ['paidAfterPeriod'] : null;
  const checks = [
    {
      key: 'flags', icon: flagsCount ? 'flag' : 'outlined_flag',
      label: flagsCount ? `Review flags · ${flagsCount}` : 'Review flags · none',
      tone: flagsCount ? 'warn' : 'ok', onClick: () => setChecksFocus('flags')
    },
    {
      key: 'sync', icon: xeroCheck.sync.live ? 'progress_activity' : 'sync', spin: xeroCheck.sync.live,
      label: xeroCheck.sync.live ? 'Syncing with Xero…' : 'Sync with Xero', onClick: () => setChecksFocus('sync')
    }
  ];
  if (step === 1) {
    checks.push({ key: 'excluded', icon: 'person_off', label: 'Excluded contacts', onClick: openExclusions });
  }
  if (generated) {
    // The Xero check is no part of approving an interim run: no chip on Review.
    if (!(interim && step === 3)) checks.push({
      key: 'check', icon: xeroCheck.check.live ? 'progress_activity' : 'fact_check', spin: xeroCheck.check.live,
      label: interim ? `Xero check (for information) · ${integritySummary.checked ? `${integritySummary.match} match` : 'not run'}`
        : integritySummary.mismatchUnacknowledged ? `Xero check · ${integritySummary.mismatchUnacknowledged} don’t match`
          : integritySummary.checked ? `Xero check · ${integritySummary.match} match` : 'Xero check · not run',
      tone: !interim && integritySummary.mismatchUnacknowledged ? 'bad' : (integritySummary.checked && !interim ? 'ok' : undefined),
      title: checkText,
      onClick: () => setChecksFocus('check')
    });
    if (!interim) {
      checks.push({
        key: 'accounts', icon: accounts?.confirmed ? 'verified' : 'fact_check',
        label: accounts?.confirmed ? 'Accounts · confirmed' : (accounts && accounts.required === false ? 'Accounts · not required' : 'Accounts · not confirmed'),
        tone: accounts?.confirmed ? 'ok' : (accounts && accounts.required === false ? undefined : 'warn'),
        onClick: () => setChecksFocus('accounts')
      });
    }
    if (figuresAsAt) {
      checks.push({
        key: 'figures', icon: figuresAsAt.stale ? 'warning' : 'cloud_done',
        label: `Payments as at ${asAtText(figuresAsAt.paymentsAsAt)}`,
        tone: figuresAsAt.stale ? 'warn' : undefined, onClick: () => setChecksFocus('figures')
      });
    }
  }

  const checkSections = [
    { key: 'flags', title: interim ? 'Review flags (interim: payments after the month end are in each statement’s “Since”)' : 'Review flags', node: <ReviewFlagsPanel context="statements" from={dates.start} to={dates.end} hide={flagsHide} /> },
    {
      key: 'sync', title: 'Sync with Xero',
      node: <StatementSyncPanel job={xeroCheck.sync.job} last={xeroCheck.sync.last} onSync={xeroCheck.sync.start} disabledReason={xeroCheck.sync.disabledReason} />
    },
    generated ? {
      key: 'check', title: interim ? 'Xero check — for information on an interim run' : 'Xero check',
      node: (
        <div className={`stmt-check-bar${!interim && integritySummary.mismatchUnacknowledged ? ' bad' : ''}`}>
          <span className={`material-symbols-rounded${xeroCheck.check.live ? ' stmt-integrity-spin' : ''}`}>
            {xeroCheck.check.live ? 'progress_activity' : integritySummary.mismatchUnacknowledged ? 'error' : 'fact_check'}
          </span>
          <span className="stmt-check-text">{checkText}</span>
          <div className="buttons slideout">
            <div>
              <button type="button" className="btn-slideout" data-write-action onClick={xeroCheck.check.start}
                disabled={!!xeroCheck.check.disabledReason}
                title={xeroCheck.check.disabledReason || 'Check every statement against this system’s data and Xero again'}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">fact_check</span></div>
                <span className="btn-label">Re-check all</span>
              </button>
            </div>
          </div>
        </div>
      )
    } : null,
    generated && !interim ? { key: 'accounts', title: 'Accounts', node: accountsPanel('drawer') } : null,
    figuresAsAt ? { key: 'figures', title: 'How fresh the figures are', node: <FiguresAsAtNote stamp={figuresAsAt} /> } : null
  ].filter(Boolean);

  // Where the run can go from here.
  const stepList = [1, 2, 3, 4].map(n => ({
    n,
    name: STEP_NAMES[n],
    reachable: n === 1 || (n <= 3 ? generated : generated && allStatementsChecked),
    why: n === 4 && generated ? 'Approve or unapprove every statement first' : 'Generate the statements first'
  }));

  // ── Footer, per step ─────────────────────────────────────────────────────
  const sendBlock = (() => {
    if (!runId) return 'Generate the statements first';
    if (accountsSendReason) return accountsSendReason;
    if (!approvedCount) return 'Nothing approved to send';
    if (emailDestination === 'groups') {
      if (plan.neither.length) return `Choose where the ${plan.neither.length} customer${plan.neither.length === 1 ? '' : 's'} in neither group go`;
      if (!plan.emailIds.length && !plan.printIds.length) return 'Nothing to send';
    }
    return null;
  })();
  const sendLabel = {
    groups: `Send — email ${plan.emailIds.length}, print ${plan.printIds.length}`,
    customers: 'Email customers',
    print: 'Send to print',
    office: 'Email to the office',
    save: 'Download ZIP'
  }[emailDestination] || 'Send';
  const sendIcon = { groups: 'send', customers: 'forward_to_inbox', print: 'print', office: 'mail', save: 'download' }[emailDestination] || 'send';

  const setupBlock = !selectedContacts.length ? 'Add the customers to send statements to'
    : !datesComplete(dates) ? 'Choose the month'
      : kindBlock || (xeroCheck.sync.live ? 'Wait for the sync with Xero to finish' : null);

  const footer = (() => {
    if (step === 1) {
      return {
        status: setupBlock && selectedContacts.length ? setupBlock
          : `${RUN_KIND_LABEL[runKind]} · ${selectedContacts.length} customer${selectedContacts.length === 1 ? '' : 's'} · ${periodLabel(dates)}`,
        statusTone: setupBlock && selectedContacts.length ? 'bad' : undefined,
        next: generated
          ? { label: 'Next: Contents', onClick: () => setStep(2) }
          : { label: loading ? 'Generating…' : 'Generate statements', icon: 'description', onClick: generateStatements, disabled: loading || !!setupBlock, title: setupBlock || undefined, writeAction: true }
      };
    }
    if (step === 2) {
      return {
        back: { label: 'Set up', onClick: () => setStep(1) },
        status: 'Review’s previews and the send use these choices.',
        next: { label: 'Next: Review', onClick: () => setStep(3) }
      };
    }
    if (step === 3) {
      return {
        back: { label: 'Contents', onClick: () => setStep(2) },
        status: toReview ? `${toReview} still to review · ${approvedCount} approved` : `All reviewed · ${approvedCount} approved`,
        statusTone: !interim && integritySummary.mismatchUnacknowledged ? 'bad' : (toReview ? undefined : 'ok'),
        statusIcon: !interim && integritySummary.mismatchUnacknowledged ? 'error' : (toReview ? 'pending' : 'check'),
        progress: statements.length ? Math.round(((statements.length - toReview) / statements.length) * 100) : null,
        next: { label: 'Next: Send', onClick: () => setStep(4), disabled: !allStatementsChecked, title: !allStatementsChecked ? 'Approve or unapprove every statement first' : undefined }
      };
    }
    return {
      back: { label: 'Review', onClick: () => setStep(3), disabled: loading },
      status: sendBlock || (loading ? (syncMessage || 'Working…') : `${approvedCount} approved statement${approvedCount === 1 ? '' : 's'} ready`),
      statusTone: sendBlock ? 'warn' : undefined,
      next: { label: sendLabel, icon: sendIcon, onClick: initiateEmailStatements, disabled: loading || !!sendBlock, title: sendBlock || undefined, writeAction: true }
    };
  })();

  // ── Send: the checks it waits for, each with a state ─────────────────────
  const sendGates = [
    allStatementsChecked
      ? { key: 'review', state: 'ok', text: 'Every statement approved or unapproved' }
      : { key: 'review', state: 'need', text: `${toReview} statement${toReview === 1 ? '' : 's'} still to review` },
    { key: 'check', ...(interim
      ? { state: 'info', text: 'Xero check — for information on an interim run' }
      : xeroCheck.check.live
        ? { state: 'wait', text: 'The Xero check is running — sending waits for it, or asks' }
        : integritySummary.checked
          ? { state: 'ok', text: `Xero check finished (${integritySummary.match} match)` }
          : { state: 'info', text: 'Not checked against Xero' }) },
    { key: 'accounts', ...(interim
      ? { state: 'info', text: 'The Accounts confirmation isn’t needed on an interim run' }
      : accounts && accounts.required === false
        ? { state: 'info', text: accountsNotRequiredText() }
        : accounts?.confirmed
          ? { state: 'ok', text: `Accounts confirmed${accounts.byName ? ` by ${accounts.byName}` : ''}` }
          : sendingToCustomers
            ? { state: 'need', text: `Accounts confirmation — “${accountsConfirmationText(accountsPeriodEnd)}”` }
            : { state: 'info', text: 'The Accounts confirmation isn’t needed for an office copy or a ZIP' }) },
    { key: 'figures', ...(figuresAsAt?.stale
      ? { state: 'info', text: 'Payments over two hours old — sending asks you to confirm' }
      : { state: 'ok', text: figuresAsAt ? `Payments as at ${asAtText(figuresAsAt.paymentsAsAt)}` : 'Payments brought in from Xero when sent' }) },
    ...((emailDestination === 'customers' || (emailDestination === 'groups' && plan.emailIds.length))
      ? [{ key: 'password', state: 'info', icon: 'key', text: 'Your password is asked before customers are emailed' }] : [])
  ];

  const sendProgress = (
    <>
      {statementsWithoutEmail.length > 0 && emailDestination === 'customers' && (
        <div className="step-result warn">
          <div className="warning-message">
            <span className="material-symbols-rounded">warning</span>
            <strong>Missing email addresses</strong>
            <p style={{ margin: '10px 0' }}>These customers have no email address and cannot be emailed — send by contact group to print them instead:</p>
            <ul style={{ marginLeft: '20px', marginBottom: '10px' }}>
              {statementsWithoutEmail.map(stmt => (
                <li key={stmt.id}>{stmt.accountNo ? <><strong>{stmt.accountNo}</strong> - </> : null}{stmt.account}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
      {syncProgress > 0 && (
        <div className="loading-bar-container">
          <LoadingBar progress={syncProgress} message={syncMessage} />
        </div>
      )}
      {!emailResult && unconfirmedPrint && !loading && (
        <div className="step-result warn">
          <div className="success-message warn">
            <span className="material-symbols-rounded">warning</span>
            {printHeadline(unconfirmedPrint)}
          </div>
          <div className="step-result-actions">
            <button type="button" className="btn-slideout" onClick={() => openPrintSummary(unconfirmedPrint)}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">list_alt</span></div>
              <span className="btn-label">Show what didn't print</span>
            </button>
          </div>
        </div>
      )}
      {emailResult && (
        <div className={`step-result${emailResult.printProblem ? ' warn' : ''}`}>
          <div className={`success-message${emailResult.printProblem ? ' warn' : ''}`}>
            <span className="material-symbols-rounded">{emailResult.printProblem ? 'warning' : 'check_circle'}</span>
            {emailResult.message}
          </div>
          {emailResult.printProblem && (
            <div className="step-result-actions">
              <button type="button" className="btn-slideout" onClick={() => openPrintSummary(emailResult)}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">list_alt</span></div>
                <span className="btn-label">Show what didn't print</span>
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );

  const otherRuns = (mine.runs || []).filter(r => Number(r.runId) !== Number(runId));
  const headerTools = (
    <>
      <div>
        <button type="button" className="btn-slideout always-out" onClick={() => openHandOver(false)} disabled={!!handOverReason}
          title={handOverReason || 'Hand this run over to a colleague — everything done so far goes with it'}>
          <div className="icon-wrapper"><span className="material-symbols-rounded">handshake</span></div>
          <span className="btn-label">Hand over…</span>
        </button>
      </div>
      {generated && !interim && (
        <div>
          <button type="button" className="btn-slideout always-out" onClick={() => openHandOver(true)} disabled={!!accountsHandOverReason}
            title={accountsHandOverReason || 'Hand the approved statements to Accounts to check before they go out'}>
            <div className="icon-wrapper"><span className="material-symbols-rounded">fact_check</span></div>
            <span className="btn-label">To Accounts</span>
          </button>
        </div>
      )}
      {otherRuns.length > 0 && (
        <div>
          <button type="button" className="btn-slideout" onClick={() => setShowMyRuns(true)} data-read-safe
            title={`The other statement runs you are on (${otherRuns.length})`}>
            <div className="icon-wrapper"><span className="material-symbols-rounded">folder_shared</span></div>
            <span className="btn-label">Your runs ({otherRuns.length})</span>
          </button>
        </div>
      )}
      <div>
        <button type="button" className="btn-slideout" onClick={() => setShowResetConfirm(true)} disabled={loading}
          title="Reset — start again from step 1 (the statements already made stay in the database)">
          <div className="icon-wrapper"><span className="material-symbols-rounded">restart_alt</span></div>
          <span className="btn-label">Reset</span>
        </button>
      </div>
    </>
  );

  // "1–30 Sep 2026 · 20 statements" under the run's name.
  const metaDates = (() => {
    if (!datesComplete(dates)) return null;
    const sd = new Date(`${dates.start}T12:00:00Z`);
    const ed = new Date(`${dates.end}T12:00:00Z`);
    const mon = (d) => d.toLocaleDateString('en-GB', { month: 'short', timeZone: 'UTC' });
    return sd.getUTCMonth() === ed.getUTCMonth() && sd.getUTCFullYear() === ed.getUTCFullYear()
      ? `${sd.getUTCDate()}–${ed.getUTCDate()} ${mon(ed)} ${ed.getUTCFullYear()}`
      : `${sd.getUTCDate()} ${mon(sd)} – ${ed.getUTCDate()} ${mon(ed)} ${ed.getUTCFullYear()}`;
  })();
  const runMeta = [
    metaDates,
    generated
      ? `${statements.length} statement${statements.length === 1 ? '' : 's'}`
      : `${selectedContacts.length} customer${selectedContacts.length === 1 ? '' : 's'} · not generated yet`
  ].filter(Boolean).join(' · ');

  const runTitle = runId && work?.period?.label && !work.isDraft
    ? `${work.period.label} statements`
    : (generated ? `${periodLabel(dates)} statements` : 'New statement run');

  // Slideout buttons
  useSlideOutButton([step, selectedContacts.length, statements.length, showPasswordModal, showNoteModal, emailResult, unconfirmedPrint, handOverReason, mine.notices.length, shownAttachments.length, xeroCheck.sync.live, xeroCheck.check.live, checksFocus, emailDestination]);

  return (
    <div className="generate-statements sr-root">
      {/* Counts the review flags for their chip (the window shows them). */}
      <div hidden>
        <ReviewFlagsPanel context="statements" from={dates.start} to={dates.end} hide={flagsHide} onCountChange={setFlagsCount} />
      </div>

      <StatementRunFrame
        title={runTitle}
        meta={runMeta}
        kind={{ label: RUN_KIND_LABEL[runKind], interim }}
        people={(
          <StatementRunPeople
            variant="avatars"
            work={work && Number(work.runId) === Number(runId) ? work : null}
            meId={meId}
          />
        )}
        steps={stepList}
        step={step}
        onStep={setStep}
        tools={headerTools}
        checks={checks}
        notices={(
          <>
            <StatementHandOverNotices
              notices={mine.notices}
              runs={mine.runs}
              currentRunId={runId}
              meId={meId}
              openBlock={loading ? 'Wait until the current send, print or generation has finished.' : null}
              onOpen={askToOpen}
              onDismiss={dismissNotice}
              handedAway={mine.handedAway}
              onTakeBack={(r) => takeRun(r, 'take_back')}
              inlineLauncher={false}
              runsOpen={showMyRuns}
              onRunsOpenChange={setShowMyRuns}
            />
            <StatementRunPeople
              work={work && Number(work.runId) === Number(runId) ? work : null}
              meId={meId}
              showPeople={false}
              onTakeOver={() => takeRun({ runId }, 'take_over')}
            />
            {step === 3 && accounts && work?.values?.stage === 'accounts' && !interim && accountsPanel('banner')}
          </>
        )}
        footer={footer}
      >
        {step === 1 && (
          <StatementStepSetup
            runKind={runKind}
            onRunKind={chooseRunKind}
            kindReadyReason={kindReadyReason}
            kindBlock={kindBlock}
            dates={dates}
            onDate={handleDateChange}
            presets={presets}
            onPreset={choosePreset}
            selectedContacts={selectedContacts}
            contactGroups={contactGroups}
            allCustomerContacts={allCustomerContacts}
            contactDropdownValue={contactDropdownValue}
            loadingGroup={loadingGroup}
            onAddGroup={addContactGroup}
            onAddContact={addContact}
            onRemoveContact={removeContact}
            onViewContact={viewContact}
            generated={generated}
          />
        )}
        {step === 2 && (
          <StatementStepContents
            content={content}
            onContent={changeContent}
            statements={sortedStatements}
            interim={interim}
            attachments={{ shown: shownAttachments, busy: attachmentBusy, onChoose: chooseAttachments, onRemove: removeAttachment, meId }}
            template={previewTemplate}
            onTemplate={setPreviewTemplate}
          />
        )}
        {step === 3 && (
          <StatementStepReview
            active={step === 3 && !showNoteModal && !checksFocus}
            statements={sortedStatements}
            filter={reviewFilter}
            onFilter={setReviewFilter}
            showXeroCheck={!interim}
            onApprove={updateApprovalStatus}
            onNote={handleAddNote}
            onBulk={(what) => (typeof what === 'string' ? bulkApproval(what) : bulkApprovalIds(what.ids, what.action, { scope: what.scope }))}
            content={content}
            template={previewTemplate}
            onTemplate={setPreviewTemplate}
            table={{
              includeInvoiceIds,
              onToggleInclude: (id) => {
                // Ticked = bundled. The run keeps the ones left out.
                const leaveOut = !excludedInvoiceIds.has(id);
                setExcludedInvoiceIds(prev => {
                  const next = new Set(prev);
                  if (leaveOut) next.add(id); else next.delete(id);
                  return next;
                });
                pushWork({ excludedInvoices: leaveOut ? { add: [id] } : { remove: [id] } });
              },
              // The bulk bar: bundle (or leave off) the ticked customers' invoices.
              onSetInclude: (ids, include) => {
                setExcludedInvoiceIds(prev => {
                  const next = new Set(prev);
                  ids.forEach(id => { if (include) next.delete(id); else next.add(id); });
                  return next;
                });
                pushWork({ excludedInvoices: include ? { remove: ids } : { add: ids } });
              },
              getRowClassName: xeroCheck.rowClass,
              onIntegrityClick: xeroCheck.openIntegrity,
              integrityAvailable: xeroCheck.meta.integrityAvailable,
              integrityReason: xeroCheck.meta.integrityReason,
              integrityPeriodEndLabel: periodEndLabel,
              sortField: statementSortField,
              sortDirection: statementSortDirection,
              onSort: (field) => {
                if (field === statementSortField) {
                  setStatementSortDirection(statementSortDirection === 'asc' ? 'desc' : 'asc');
                } else {
                  setStatementSortField(field);
                  setStatementSortDirection('asc');
                }
              }
            }}
          />
        )}
        {step === 4 && (
          <div onClick={clearLoadingBar} className="sr-send-wrap">
            <StatementStepSend
              destination={emailDestination}
              onDestination={chooseDestination}
              statements={statements}
              routeChoices={routeChoices}
              onRouteChoice={chooseRoute}
              content={content}
              includeInvoiceIds={includeInvoiceIds}
              templateMode={templateMode}
              onTemplateMode={(t) => { setTemplateMode(t); pushWork({ templateMode: t }); }}
              officeEmail={customEmail}
              onOfficeEmail={changeOfficeEmail}
              gates={sendGates}
              progress={sendProgress}
              accounts={!interim && accounts && accounts.required !== false && sendingToCustomers ? accountsPanel('send') : null}
            />
          </div>
        )}
      </StatementRunFrame>

      <StatementChecksDrawer focus={checksFocus} sections={checkSections} onClose={() => setChecksFocus(null)} />
      {/* Contacts the run will skip. Rows open the contact modal, whose own
          Edit button lets the tick be cleared without leaving this screen. */}
      {showExclusions && (
        <Modal
          isOpen={true}
          onClose={() => setShowExclusions(false)}
          title="Contacts Not Receiving Statements"
          size="md"
          className="statement-exclusions"
        >
          <div className="stmt-exclusions-note">
            <span className="material-symbols-rounded">info</span>
            <span>
              These contacts are skipped when statements are generated. To add a contact
              to this list, edit it via <strong>Search Contacts</strong> and untick
              &ldquo;Statement&rdquo;. Click any row below to open that contact and edit it.
            </span>
          </div>

          {loadingExclusions ? (
            <LoadingSpinner />
          ) : exclusions.length === 0 ? (
            <p className="stmt-exclusions-empty">Every contact currently receives statements.</p>
          ) : (
            <div className="stmt-exclusions-list">
              {exclusions.map(c => (
                <button
                  key={c.id}
                  type="button"
                  className="stmt-exclusion-row"
                  onClick={() => {
                    setViewingContactId(c.id);
                    setShowContactModal(true);
                  }}
                >
                  <span className={`account-badge-inline${!c.account_no ? ' empty' : ''}`}>
                    {c.account_no || ''}
                  </span>
                  <span className="stmt-exclusion-name">{c.company_name}</span>
                </button>
              ))}
            </div>
          )}
        </Modal>
      )}

      {/* Contact View Modal */}
      {showContactModal && viewingContactId && (
        <ContactModal
          contactId={viewingContactId}
          onClose={() => {
            setShowContactModal(false);
            setViewingContactId(null);
          }}
        />
      )}

      {/* Password Confirmation Modal */}
      {showPasswordModal && createPortal(
        <div className="modal-overlay" onClick={() => {
          setShowPasswordModal(false);
          setPassword('');
        }}>
          <div className="modal-content modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Confirm Bulk Email</h2>
              <button onClick={() => {
                setShowPasswordModal(false);
                setPassword('');
              }} className="modal-close"  style={{ marginRight: '5px' }}>
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>
            <div className="modal-body" style={{ padding: 'var(--spacing-xl)' }}>
              <p style={{ margin: '-20px 0 var(--spacing-lg)', color: 'var(--color-error)', display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', fontSize: '0.85em' }}>
                <span className="material-symbols-rounded" style={{ fontSize: '24px' }}>warning</span> Emailing {emailDestination === 'groups' ? plan.emailIds.length : approvedCount} customer/s{emailDestination === 'groups' && plan.printIds.length ? `, then printing ${plan.printIds.length}` : ''}
              </p>
              <p style={{ marginBottom: 'var(--spacing-md)', fontSize: 'var(--font-md)', color: 'var(--text-secondary)' }}>
                Enter your password to confirm:
              </p>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handlePasswordConfirm();
                  }
                }}
                style={{ width: '100%' }}
                autoFocus
              />
              <div className="modal-actions-row" style={{ marginTop: "var(--spacing-xl", gap: "var(--spacing-xs)", display: "flex", justifyContent: "space-between" }}>
                <button data-read-safe
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPassword('');
                  }}
                  className="btn-round btn-danger"
                >
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">close</span>
                  </div>
                  <span className="btn-label">Cancel</span>
                </button>
                <button
                  onClick={handlePasswordConfirm}
                  className="btn-round"
                  disabled={!password}
                >
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">send</span>
                  </div>
                  <span className="btn-label">Confirm & Send</span>
                </button>
              </div>
            </div>
          </div>
        </div>, document.body
      )}

      {/* The note window: office note + note for the customer */}
      {showNoteModal && statements.some(st => st.id === noteStatementId) && (
        <StatementNoteDialog
          statement={statements.find(st => st.id === noteStatementId)}
          officeReady={officeNotes.ready}
          officeReason={officeNotes.reason}
          onClose={closeNoteDialog}
          onSaved={applyNoteSaved}
        />
      )}

      <ConfirmDialog
        isOpen={showResumePrompt}
        title="Resume Statement Run?"
        type="info"
        message={`You have an in-progress statement run (step ${step} of 4 — ${STEP_NAMES[step] || ''}, ${selectedContacts.length} contacts). Would you like to continue where you left off or start fresh?`}
        confirmText="Continue"
        cancelText="Start Fresh"
        onConfirm={() => setShowResumePrompt(false)}
        onCancel={() => {
          setShowResumePrompt(false);
          resetStepsFrom(1);
          setStepState(1);
          setDates(getPreviousMonthRange());
        }}
      />

      {showHandOver && (
        <StatementHandOverDialog
          mode={handOverToAccounts ? 'accounts' : 'handover'}
          initialMessage={handOverToAccounts ? ACCOUNTS_HANDOVER_MESSAGE : ''}
          confirmationText={accountsConfirmationText(accountsPeriodEnd)}
          subject={handOverSubject}
          stage={handOverStage}
          people={work && Number(work.runId) === Number(runId) ? (work.people || []) : []}
          onHandOver={submitHandOver}
          onClose={() => setShowHandOver(false)}
        />
      )}

      <ConfirmDialog
        isOpen={!!openConfirm}
        title="Open this statement run?"
        type="info"
        message={openConfirm?.message || ''}
        confirmText="Open it"
        cancelText="Not now"
        onConfirm={() => { const t = openConfirm?.target; setOpenConfirm(null); if (t) openRun(t); }}
        onCancel={() => setOpenConfirm(null)}
      />

      <ConfirmDialog
        isOpen={!!staleConfirm}
        title="Payments not up to date"
        type="warning"
        message={staleConfirm ? `${staleConfirm.message} Your confirmation is recorded in Activity.` : ''}
        confirmText="Send anyway"
        cancelText="Cancel"
        onConfirm={() => {
          const args = staleConfirm?.args || {};
          setStaleConfirm(null);
          if (args.group) runGroupSend({ ...args.group, confirmStalePayments: true });
          else handleEmailStatements({ ...args, confirmStalePayments: true });
        }}
        onCancel={() => setStaleConfirm(null)}
      />

      <ConfirmDialog
        isOpen={showResetConfirm}
        title="Reset Workflow?"
        type="warning"
        message="This will clear your selected contacts, the current statement run, and any in-progress send/save/print state. The statements themselves remain in the database — this only resets the workflow on this screen. Continue?"
        confirmText="Reset"
        cancelText="Cancel"
        onConfirm={handleResetWorkflow}
        onCancel={() => setShowResetConfirm(false)}
      />

      {xeroCheck.dialogs}

      <AlertDialog
        isOpen={alertDialog.isOpen}
        message={alertDialog.message}
        type={alertDialog.type}
        title={alertDialog.title}
        onClose={() => {
          const onCloseAction = alertDialog.onCloseAction;
          setAlertDialog({ ...alertDialog, isOpen: false, onCloseAction: undefined });
          if (onCloseAction) onCloseAction();
        }}
      />
    </div>
  );
}

export default GenerateStatements;
